import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import useAppStore from './store/appStore';
import useAdminStore from './store/adminStore'; 
import { AnimatePresence, motion } from 'framer-motion';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import getAppPages from './config/pageRegistry';
import SmartSidebar from './components/SmartSidebar';
import ZoomControls from './components/ZoomControls';
import TeamPortalChrome from './components/TeamPortalChrome';
import ProductivityDock from './components/ProductivityDock'; 
import SystemBootSequence from './components/SystemBootSequence';
import BettyLiveOrb from './components/live/BettyLiveOrb';
import PageVideoHelp from './components/common/PageVideoHelp';
import PagePinGate from './components/security/PagePinGate';

import { auth, db } from './config/firebase';
import { onAuthStateChanged, User, signOut } from 'firebase/auth'; 
import { doc, getDoc, onSnapshot, updateDoc, setDoc, serverTimestamp, deleteField, increment } from 'firebase/firestore';
import AuthGatewayPage, { capturePwaReturnUrl } from './pages/AuthGatewayPage';
import CoachIntakeAdminPage from './pages/CoachIntakeAdminPage'; 
import { resolveTeamMemberByEmail, isAdminPortalAccess } from './utils/teamAuth';
import { installNumberInputKeyGuard } from './utils/numberInputKeyGuard';
import {
    refreshFcmTokenIfAllowed,
    clearPushBadges,
} from './services/pushNotifications';
import { migrateLocalStateToCloud, scheduleAutosync, getSyncEpoch, cancelPendingAutosync } from './config/firebaseSync';
import { migrateTeamRegistry } from './store/slices/coreDataSlice';
import { mergeWithDefaultSettings } from './store/slices/settingsSlice';
import {
    ADMIN_LOCK_HEARTBEAT_MS,
    ADMIN_LOCK_IDLE_MS,
    SESSION_HEARTBEAT_MS,
    buildAdminLockPayload,
    checkRemoteSessionBlocking,
    clearOwnedActiveSession,
    getOrCreateLocalSessionId,
    heartbeatActiveSession,
    heartbeatAdminMaintenanceLock,
    isAdminMaintenanceLockLive,
    isForeignSessionBlocking,
    releaseAdminMaintenanceLock,
    resolveAdminLockHolderLabel,
    tryClaimActiveSession,
    resolveActiveSessionHolderLabel,
    type AdminLockHolder,
    type ActiveSession,
} from './utils/sessionLock';
import { adminUpdateMandantRoot } from './config/adminOverwriteGuard';
import AdminDashboard from './pages/AdminDashboard';
import CoachDashboard from './pages/CoachDashboard';
import { ErrorBoundary } from './components/ErrorBoundary';

import OnboardingWizard from './pages/OnboardingWizard'; 
import PePapersPage from './pages/PePapersPage';
import PremiumModal from './components/PremiumModal';
import ZahlungsstopModal from './components/ZahlungsstopModal';
import { RIGHT_EDGE_RAIL_SLOT_ID } from './components/RightEdgeRailSlot';
import { shouldEnforcePaymentLock } from './utils/stripeBilling';
import AutoUpdateModal from './components/AutoUpdateModal';
import EventPromotionModal from './components/modals/EventPromotionModal';
import ClientPcSafeManager from './components/ClientPcSafeManager';

// --- ICONS ---
const LogOutIcon = ({ size = 16, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
);

const DownloadCloudIcon = ({ size = 16, className = "" }: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242"/><path d="M12 12v9"/><path d="m8 17 4 4 4-4"/>
  </svg>
);

const SunIcon = ({ size = 16, className = "" }: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
  </svg>
);

const MoonIcon = ({ size = 16, className = "" }: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const JahresAkteIcon = ({ size = 16, className = "" }: any) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
  </svg>
);

const ChatIcon = ({ size = 24, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/>
    </svg>
);

const SendIcon = ({ size = 16, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
);

const XIcon = ({ size = 16, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
);

const MicIcon = ({ size = 16, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>
    </svg>
);

const StopIcon = ({ size = 16, className = "" }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    </svg>
);

const Notification: React.FC<{ message: string; type: 'success' | 'error'; onClear: () => void }> = ({ message, type, onClear }) => {
    useEffect(() => { if (message) { const timer = setTimeout(() => { onClear(); }, 4000); return () => clearTimeout(timer); } }, [message, onClear]);
    if (!message) return null;
    const bgClass = type === 'error' ? 'bg-red-600 border-red-400' : 'bg-green-600 border-green-400';
    return <div className={`fixed top-24 right-5 ${bgClass} text-white font-bold py-3 px-6 rounded-xl shadow-2xl z-[100] animate-in slide-in-from-top-5 border border-white/20`}>{message}</div>;
};

// ------------------------------------------------------------------------------------------------
// BETTY CHAT WINDOW COMPONENT (INLINE)
// ------------------------------------------------------------------------------------------------
interface BettyChatWindowProps {
    currentRouteName: string;
    showNotification: (msg: string, type: 'success' | 'error') => void;
}

const BettyChatWindowComponent: React.FC<BettyChatWindowProps> = ({ currentRouteName, showNotification }) => {
    const [bettyInput, setBettyInput] = useState('');
    const [isListening, setIsListening] = useState(false);
    
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const recognitionRef = useRef<any>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const prevInputRef = useRef('');
    const isSendingRef = useRef(false);
    /** God-Mode: Store-Heartbeats → Re-Renders; Chrome bricht continuous oft ab → gezielt neu starten */
    const wantListeningRef = useRef(false);
    const restartTimerRef = useRef<number | null>(null);

    const isBettyOpen = useAppStore((state: any) => state.isBettyOpen);
    const isBettyLoading = useAppStore((state: any) => state.isBettyLoading);
    const bettyMessages = useAppStore((state: any) => state.bettyMessages);
    const toggleBetty = useAppStore((state: any) => state.toggleBetty);
    const closeBetty = useAppStore((state: any) => state.closeBetty);
    const sendBettyMessage = useAppStore((state: any) => state.sendBettyMessage);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto'; 
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`; 
        }
    }, [bettyInput, isBettyOpen]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [bettyMessages, isBettyLoading, isBettyOpen, bettyInput]);

    const stopMicHardware = () => {
        if (streamRef.current) {
            try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
            streamRef.current = null;
        }
    };

    const stopListeningFully = () => {
        wantListeningRef.current = false;
        if (restartTimerRef.current != null) {
            window.clearTimeout(restartTimerRef.current);
            restartTimerRef.current = null;
        }
        if (recognitionRef.current) {
            try { recognitionRef.current.onend = null; } catch (_) { /* ignore */ }
            try { recognitionRef.current.abort(); } catch (_) { /* ignore */ }
            recognitionRef.current = null;
        }
        stopMicHardware();
        setIsListening(false);
    };

    useEffect(() => {
        return () => {
            stopListeningFully();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** SpeechRecognition synchron zum Klick starten (kein await davor — sonst God-Mode/Chrome: not-allowed). */
    const startRecognitionEngine = () => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            showNotification("Dein Browser unterstützt diese Diktierfunktion leider nicht.", "error");
            wantListeningRef.current = false;
            setIsListening(false);
            return;
        }

        if (recognitionRef.current) {
            try { recognitionRef.current.onend = null; } catch (_) { /* ignore */ }
            try { recognitionRef.current.abort(); } catch (_) { /* ignore */ }
            recognitionRef.current = null;
        }

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'de-DE';

        recognition.onstart = () => {
            if (!wantListeningRef.current) return;
            setIsListening(true);
        };

        recognition.onresult = (event: any) => {
            if (isSendingRef.current || !wantListeningRef.current) return;

            let interimTranscript = '';
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; ++i) {
                if (event.results[i].isFinal) {
                    finalTranscript += event.results[i][0].transcript;
                } else {
                    interimTranscript += event.results[i][0].transcript;
                }
            }

            setBettyInput(prevInputRef.current + finalTranscript + interimTranscript);
            if (finalTranscript) prevInputRef.current += finalTranscript;
        };

        recognition.onerror = (event: any) => {
            // no-speech / aborted: bei Wunsch weiterhören; echte Fehler melden
            if (event.error === 'aborted' || event.error === 'no-speech') return;
            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                wantListeningRef.current = false;
                setIsListening(false);
                stopMicHardware();
                showNotification("Mikrofon-Zugriff verweigert. Bitte Browser-Berechtigung prüfen.", "error");
                return;
            }
            if (!wantListeningRef.current) return;
            showNotification(`Mikrofon: ${event.error}`, "error");
        };

        recognition.onend = () => {
            if (!wantListeningRef.current) {
                setIsListening(false);
                stopMicHardware();
                return;
            }
            // Chrome beendet continuous oft nach Stille / Tab-Aktivität (God-Mode Heartbeats) → Soft-Restart
            if (restartTimerRef.current != null) window.clearTimeout(restartTimerRef.current);
            restartTimerRef.current = window.setTimeout(() => {
                restartTimerRef.current = null;
                if (!wantListeningRef.current) return;
                try {
                    startRecognitionEngine();
                } catch (_) {
                    wantListeningRef.current = false;
                    setIsListening(false);
                }
            }, 120);
        };

        recognitionRef.current = recognition;
        try {
            recognition.start();
            setIsListening(true);
        } catch (error: any) {
            console.error('SpeechRecognition.start fehlgeschlagen:', error);
            wantListeningRef.current = false;
            setIsListening(false);
            showNotification("Fehler beim Starten des Diktats.", "error");
        }
    };

    const toggleListening = () => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) {
            showNotification("Dein Browser unterstützt diese Diktierfunktion leider nicht.", "error");
            return;
        }

        if (wantListeningRef.current || isListening) {
            stopListeningFully();
            return;
        }

        wantListeningRef.current = true;
        prevInputRef.current = bettyInput
            ? bettyInput + (bettyInput.endsWith(' ') || bettyInput.endsWith('\n') ? '' : ' ')
            : '';

        // Mic-Permission parallel warmhalten (nach Start — User-Geste bleibt für SpeechRecognition gültig)
        void navigator.mediaDevices
            ?.getUserMedia?.({ audio: true })
            .then((stream) => {
                if (!wantListeningRef.current) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                stopMicHardware();
                streamRef.current = stream;
            })
            .catch(() => {
                /* SpeechRecognition kann ohne getUserMedia-Stream laufen */
            });

        startRecognitionEngine();
        if (textareaRef.current) textareaRef.current.focus();
    };

    const handleSendBettyMessage = async () => {
        if (!bettyInput.trim() || isBettyLoading) return;
        
        isSendingRef.current = true;
        stopListeningFully();

        const textToSend = bettyInput;
        
        setBettyInput('');
        prevInputRef.current = '';
        if (textareaRef.current) {
            textareaRef.current.style.height = '48px'; 
        }

        await sendBettyMessage(textToSend, currentRouteName);
        
        setTimeout(() => {
            isSendingRef.current = false;
        }, 300);
    };

    useEffect(() => {
        if (!isBettyOpen) {
            // Fenster zu → Diktat hart stoppen (sonst Ghost-Mic im God-Mode)
            wantListeningRef.current = false;
            if (restartTimerRef.current != null) {
                window.clearTimeout(restartTimerRef.current);
                restartTimerRef.current = null;
            }
            if (recognitionRef.current) {
                try { recognitionRef.current.onend = null; } catch (_) { /* ignore */ }
                try { recognitionRef.current.abort(); } catch (_) { /* ignore */ }
                recognitionRef.current = null;
            }
            stopMicHardware();
            setIsListening(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBettyOpen]);

    return createPortal(
        <>
            {/* Mobile: Betty Live + Frag Betty als Duo mittig — Live nicht mehr rechts außerhalb */}
            <div
              className="fixed z-[10000] bottom-2.5 left-1/2 -translate-x-1/2 flex items-end gap-2 pointer-events-auto md:hidden"
              id="betty-live-mobile-duo"
            >
              <BettyLiveOrb compact currentScreenContext={currentRouteName} />
              <button
                onClick={toggleBetty}
                className="w-11 h-11 rounded-full bg-[#15171e] hover:bg-[#1a1d24] text-[#E6C299] border border-[#E6C299]/30 shadow-[0_0_20px_rgba(230,194,153,0.15)] flex items-center justify-center"
                title={isBettyOpen ? 'Betty schließen' : 'Frag Betty'}
                aria-label={isBettyOpen ? 'Betty schließen' : 'Frag Betty'}
              >
                {isBettyOpen ? <XIcon size={20} /> : <ChatIcon size={20} />}
              </button>
            </div>

            {/* Desktop: Betty-Duo rechts, klar über Zoom/Scroll — nicht mittig, nicht auf den Controls */}
            <div
                className="hidden md:flex fixed z-[10000] pointer-events-auto
                    right-4 bottom-44 flex-col items-stretch gap-2 w-[7.5rem]"
                id="betty-live-sidebar-slot"
            >
                <BettyLiveOrb currentScreenContext={currentRouteName} />
                <button
                    onClick={toggleBetty}
                    className="w-full h-10 bg-[#15171e] hover:bg-[#1a1d24] text-[#E6C299] border border-[#E6C299]/30 shadow-[0_0_15px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(230,194,153,0.2)] transition-all flex items-center justify-center gap-1 group px-1.5 rounded-full"
                    title={isBettyOpen ? 'Betty schließen' : 'Frag Betty'}
                    aria-label={isBettyOpen ? 'Betty schließen' : 'Frag Betty'}
                >
                    {isBettyOpen ? (
                        <>
                            <XIcon size={14} className="group-hover:scale-110 transition-transform shrink-0" />
                            <span className="font-bold uppercase tracking-wide text-[8px] whitespace-nowrap">Schließen</span>
                        </>
                    ) : (
                        <>
                            <ChatIcon size={14} className="group-hover:scale-110 transition-transform shrink-0" />
                            <span className="font-bold uppercase tracking-wide text-[8px] whitespace-nowrap">Frag Betty</span>
                        </>
                    )}
                </button>
            </div>

            <AnimatePresence>
                {isBettyOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="fixed left-3 right-3 md:left-auto md:right-[8.75rem] md:bottom-60 md:top-auto bottom-[4.5rem] md:w-[400px] w-auto h-[min(480px,calc(100vh-12rem))] max-h-[calc(100vh-12rem)] bg-[#0f1015] border border-[#E6C299]/30 rounded-2xl shadow-2xl z-[10001] flex flex-col overflow-hidden"
                    >
                        <div className="bg-[#15171e] border-b border-white/5 p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 flex-shrink-0 rounded-full bg-[#E6C299]/10 border border-[#E6C299]/30 flex items-center justify-center shadow-inner relative overflow-hidden">
                                    <img src="/assets/betty.png" alt="Betty" className="w-full h-full object-cover" />
                                    {isBettyLoading && (
                                        <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full animate-ping"></span>
                                    )}
                                </div>
                                <div>
                                    <h3 className="text-[#E6C299] font-black uppercase tracking-widest text-sm">Frag Betty</h3>
                                    <p className="text-slate-500 text-[10px] uppercase tracking-wider">System-KI v1.0</p>
                                </div>
                            </div>
                            <button onClick={closeBetty} className="text-slate-500 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/5">
                                <XIcon size={16} />
                            </button>
                        </div>

                        <div 
                            ref={chatContainerRef}
                            className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent"
                        >
                            {bettyMessages.map((msg: any, i: number) => (
                                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                                        msg.role === 'user' 
                                        ? 'bg-[#15171e] border border-slate-700 text-white rounded-br-sm shadow-md' 
                                        : 'bg-[#E6C299]/10 border border-[#E6C299]/20 text-[#E6C299] rounded-bl-sm shadow-md'
                                    }`}>
                                        {msg.text}
                                    </div>
                                </div>
                            ))}
                            
                            {isBettyLoading && (
                                <div className="flex justify-start">
                                    <div className="max-w-[85%] p-3 rounded-2xl text-sm bg-[#E6C299]/5 border border-[#E6C299]/10 text-[#E6C299] rounded-bl-sm flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 bg-[#E6C299] rounded-full animate-bounce"></span>
                                        <span className="w-1.5 h-1.5 bg-[#E6C299] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                                        <span className="w-1.5 h-1.5 bg-[#E6C299] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="shrink-0 p-4 bg-[#15171e] border-t border-white/5 flex flex-col gap-3">
                            <AnimatePresence>
                                {isListening && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="w-full text-center"
                                    >
                                        <div className="text-red-400 text-[10px] uppercase font-black tracking-widest bg-red-500/10 px-3 py-1.5 rounded-full border border-red-500/20 inline-flex items-center gap-2 animate-pulse shadow-[0_0_15px_rgba(239,68,68,0.1)]">
                                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                            Aufnahme läuft: Klicke STOPP zum Bearbeiten
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="flex items-end gap-2">
                                <button 
                                    onClick={toggleListening}
                                    disabled={isBettyLoading}
                                    className={`flex-shrink-0 h-[48px] rounded-xl border transition-all flex items-center justify-center font-black uppercase text-[10px] tracking-widest ${
                                        isListening 
                                        ? 'w-[90px] gap-1.5 bg-red-500/20 border-red-500 text-red-500 shadow-[0_0_15px_rgba(239,68,68,0.2)] hover:bg-red-500/30' 
                                        : 'w-[48px] bg-[#08090b] border-slate-700 text-slate-400 hover:text-[#E6C299] hover:border-[#E6C299]/50'
                                    }`}
                                    title={isListening ? "Aufnahme stoppen & bearbeiten" : "Diktieren"}
                                >
                                    {isListening ? (
                                        <>
                                            <StopIcon size={14} />
                                            STOPP
                                        </>
                                    ) : (
                                        <MicIcon size={18} />
                                    )}
                                </button>

                                <div className="relative flex-1">
                                    <textarea
                                        ref={textareaRef}
                                        value={bettyInput}
                                        onChange={(e) => setBettyInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSendBettyMessage();
                                            }
                                        }}
                                        placeholder={isListening ? "🎙️ Diktat läuft... Klicke auf 'STOPP' zum Bearbeiten" : "Nachricht..."}
                                        disabled={isBettyLoading}
                                        rows={1}
                                        className={`w-full bg-[#08090b] border text-white px-4 py-3 rounded-xl focus:outline-none placeholder-slate-600 pr-12 transition-colors text-sm font-medium disabled:opacity-50 resize-none overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent block leading-normal ${
                                            isListening ? 'border-red-500/30' : 'border-slate-700 focus:border-[#E6C299]/50'
                                        }`}
                                        style={{ minHeight: '48px' }}
                                    />
                                    <button 
                                        onClick={handleSendBettyMessage}
                                        disabled={isBettyLoading || (!bettyInput.trim() && !isListening)}
                                        className="absolute right-2 bottom-[6px] h-9 w-9 flex items-center justify-center text-[#E6C299] hover:text-white transition-colors disabled:opacity-50 disabled:hover:text-[#E6C299] bg-[#15171e] rounded-lg border border-slate-700"
                                    >
                                        <SendIcon size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>,
        document.body
    );
};

const BettyChatWindow = React.memo(BettyChatWindowComponent, (prevProps, nextProps) => {
    return prevProps.currentRouteName === nextProps.currentRouteName;
});

// ------------------------------------------------------------------------------------------------
// TEAM PORTAL ROUTING (Admin / Coach)
// ------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
// TEAM PORTAL ROUTING (Admin / Coach)
// Admin-Kommandozentrale NUR für echte Admins – Coaches immer Coach-Dashboard
// ------------------------------------------------------------------------------------------------
const TeamBootScreen: React.FC<{ label: string }> = ({ label }) => (
    <div className="min-h-screen bg-[#020617] text-slate-200 flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-sm font-black uppercase tracking-widest text-[#E6C299]">{label}</p>
        <p className="text-xs text-slate-500 max-w-xs">Kein weißer Bildschirm — die Seite baut sich noch auf.</p>
        <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 px-5 py-2.5 rounded-xl border border-slate-600 text-[10px] font-black uppercase tracking-widest text-slate-300"
        >
            Neu laden
        </button>
    </div>
);

const TeamPortalRoutes: React.FC<{ role: string | null; category: string | null; email?: string | null }> = ({
    role,
    category,
    email,
}) => {
    const location = useLocation();
    const path = String(location.pathname || window.location.pathname || '').toLowerCase();
    const wantsIntakeHub = path.includes('/coach/intake-hub');
    const wantsCoachPath = path.includes('/coach');
    const wantsPepulsePath = path.includes('/pepulse') || path.endsWith('pepulse');
    const allowAdmin = isAdminPortalAccess(role, category, email);

    let portal: React.ReactNode;
    if (wantsIntakeHub) {
        // Hub für Coaches + Admins; Übernahme (Vorabkunde) nur mit adminMode
        portal = <CoachIntakeAdminPage adminMode={allowAdmin} />;
    } else if (wantsCoachPath || wantsPepulsePath || !allowAdmin) {
        portal = <CoachDashboard />;
    } else {
        portal = <AdminDashboard />;
    }

    return <TeamPortalChrome>{portal}</TeamPortalChrome>;
};

// ------------------------------------------------------------------------------------------------
// APP MAIN COMPONENT
// ------------------------------------------------------------------------------------------------
function App(): JSX.Element | null {
    const [authUser, setAuthUser] = useState<User | null>(null);
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [isTeamResolutionComplete, setIsTeamResolutionComplete] = useState(false);
    const [currentPage, setCurrentPage] = useState<string>('jahresuebersicht');
    const currentPageRef = useRef(currentPage);
    currentPageRef.current = currentPage;
    const [notification, setNotification] = useState<{msg: string, type: 'success' | 'error'}>({ msg: '', type: 'success' });
    
    const [isAdminLocked, setIsAdminLocked] = useState(false);
    const [adminLockHolderLabel, setAdminLockHolderLabel] = useState<string | null>(null);
    const [sessionCanResume, setSessionCanResume] = useState(false);
    const [isSessionResumePending, setIsSessionResumePending] = useState(false);
    /** Wer die Akte hält (Coach-Name), wenn Session-Sperre aktiv */
    const [sessionBlockHolderLabel, setSessionBlockHolderLabel] = useState<string | null>(null);
    const sessionReclaimInFlightRef = useRef(false);
    /** Letzter Wartungs-Lock-Snapshot für TTL-Reeval ohne neuen Firestore-Event */
    const adminLockSnapRef = useRef<{
        isLockedByAdmin?: boolean;
        adminLockHeartbeat?: unknown;
        adminLockHolderUid?: unknown;
        adminLockHolderName?: unknown;
    } | null>(null);
    const staleAdminLockHealRef = useRef<string | null>(null);
    /** Mandant: vorheriger Lock-Zustand für Flush nach Freigabe */
    const wasAdminLockedRef = useRef(false);
    /** God-Mode: letzte echte User-Interaktion (für Idle-Freigabe) */
    const godModeLastActivityRef = useRef(Date.now());
    const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
    const [isDarkTheme, setIsDarkTheme] = useState(() =>
        typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : true
    );
    
    const [isYearLocked, strokeIsYearLocked] = useState(false);
    const [currentYear, setCurrentYear] = useState<string>(localStorage.getItem('CURRENT_YEAR') || new Date().getFullYear().toString());
    const [currentYearStatus, setCurrentYearStatus] = useState<string>(localStorage.getItem('CURRENT_YEAR_STATUS') || 'datenerfassung');
    const [masterJahre, setMasterJahre] = useState<any[]>([]);

    const [exitWarningModal, setExitWarningModal] = useState<{
        isOpen: boolean;
        action: 'logout' | 'stop_impersonation' | null;
        reason: 'dirty' | 'year_open' | null;
    }>({ isOpen: false, action: null, reason: null });
    /** Vor Logout: PC-Safe-Erinnerung (abbrechen = eingeloggt bleiben) */
    const [pcSafeLogoutOpen, setPcSafeLogoutOpen] = useState(false);
    /** PC-Safe-Button → Ordner wählen / speichern (wie Admin) */
    const [pcSafeManualOpen, setPcSafeManualOpen] = useState(false);

    const { 
        clientData, appSettings, isInitialized, zoomLevel, initializeStore, settings,
        isSystemBooted, setSystemBooted, team, teamRegistry,
        unlockedPremiumTools, openPremiumModal,
    } = useAppStore(useShallow((state: any) => ({
        clientData: state.clientData,
        settings: state.settings,
        appSettings: state.appSettings,
        isInitialized: state.isInitialized,
        zoomLevel: state.zoomLevel,
        initializeStore: state.initializeStore,
        isSystemBooted: state.isSystemBooted,
        setSystemBooted: state.setSystemBooted,
        team: state.team,
        teamRegistry: state.teamRegistry,
        unlockedPremiumTools: state.unlockedPremiumTools,
        openPremiumModal: state.openPremiumModal,
    })));

    const { impersonatedClientId, impersonatedClientName, stopImpersonation, isSessionBlocked, setSessionBlocked, godModeInitOwnedByStarter } = useAdminStore(
        useShallow((state: any) => ({
            impersonatedClientId: state.impersonatedClientId,
            impersonatedClientName: state.impersonatedClientName,
            stopImpersonation: state.stopImpersonation,
            isSessionBlocked: state.isSessionBlocked,
            setSessionBlocked: state.setSessionBlocked,
            godModeInitOwnedByStarter: state.godModeInitOwnedByStarter,
        }))
    );

    const userRoleInfo = useMemo(() => {
        return resolveTeamMemberByEmail(authUser?.email, team, teamRegistry);
    }, [authUser, team, teamRegistry]);

    // Nur Impersonation zählt — nicht an isTeam koppeln (team kann kurz leer sein und warf Admins zurück)
    const isShadowing = !!impersonatedClientId;

    /** Anzeigename für Session-Claim/Heartbeat (Gott-Modus) → activeSession.holderName */
    const resolveSessionHolderName = useCallback((): string => {
        const email = String(authUser?.email || '').trim().toLowerCase();
        const pool = [...(team || []), ...(teamRegistry || [])];
        const member =
            (authUser?.uid
                ? pool.find((m: any) => String(m?.id || '').trim() === authUser.uid)
                : null) ||
            pool.find((m: any) => String(m?.email || '').trim().toLowerCase() === email) ||
            null;
        return (
            String(member?.name || '').trim() ||
            String(authUser?.displayName || '').trim() ||
            (email ? email.split('@')[0] : '') ||
            'PreisExpert-Team'
        );
    }, [authUser, team, teamRegistry]);

    /** Label aus fremder Session + Fallback Team-Registry per userId */
    const labelFromActiveSession = useCallback(
        (active: Partial<ActiveSession> | null | undefined): string | null => {
            const fromSession = resolveActiveSessionHolderLabel(active);
            if (fromSession && fromSession !== 'PreisExpert-Team') return fromSession;
            const uid = String(active?.userId || '').trim();
            if (!uid) return fromSession;
            const pool = [...(team || []), ...(teamRegistry || [])];
            const member = pool.find((m: any) => String(m?.id || '').trim() === uid);
            const name = String(member?.name || '').trim();
            if (name) return name;
            return fromSession;
        },
        [team, teamRegistry]
    );

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setAuthUser(user);
            setIsAuthLoading(false);
            if (!user) {
                setIsTeamResolutionComplete(false);
                // Logout: God-Mode-Leiche töten (Strg+F5 löscht das NICHT von allein)
                try { sessionStorage.removeItem('GOD_MODE_ID'); } catch (_) { /* ignore */ }
                useAdminStore.setState({ impersonatedClientId: null, impersonatedClientName: null });
                // PWA/Deep-Link (z. B. /pepulse) vor Auth-Gateway sichern — nicht mit "/" überschreiben
                capturePwaReturnUrl();
            }
        });
        return () => unsubscribe(); 
    }, []);

    // Uneingeloggt: Zielroute sofort sichern (iOS Standalone / Manifest start_url)
    useEffect(() => {
        if (isAuthLoading) return;
        if (!authUser) {
            capturePwaReturnUrl();
        }
    }, [authUser, isAuthLoading]);

    // NumLock-aus: ArrowUp/Down auf allen <input type="number"> unterbinden (app-weit)
    useEffect(() => installNumberInputKeyGuard(), []);

    // Auth/Team-Bootstrap — läuft NUR bei Login, NICHT bei God-Mode-Klick
    useEffect(() => {
        if (!authUser) return;

        let cancelled = false;

        sessionStorage.setItem('CURRENT_YEAR', currentYear);
        localStorage.setItem('CURRENT_YEAR', currentYear);

        // God-Mode läuft: Team-Resolution nicht auf false setzen (sonst weißer Screen / Rücksprung)
        const alreadyGod = !!useAdminStore.getState().impersonatedClientId
            || (() => { try { return !!sessionStorage.getItem('GOD_MODE_ID'); } catch { return false; } })();
        if (!alreadyGod) {
            setIsTeamResolutionComplete(false);
        }

        (async () => {
            let teamData: any[] = [];
            let teamRegistryData: any[] = [];
            let finalSettings = useAppStore.getState().settings;
            let appSettings = useAppStore.getState().appSettings || {};

            try {
                let sysRef = doc(db, 'system_data', 'global_settings');
                let sysDoc = await getDoc(sysRef);
                if (!sysDoc.exists()) {
                    sysRef = doc(db, 'system_data', 'master');
                    sysDoc = await getDoc(sysRef);
                }
                const sysData = sysDoc.exists() ? sysDoc.data() : null;
                const state = useAppStore.getState();
                finalSettings = mergeWithDefaultSettings(sysData?.settings || state.settings);
                teamData = sysData?.team || state.team || [];
                teamRegistryData = migrateTeamRegistry(
                    teamData,
                    sysData?.teamRegistry || state.teamRegistry
                );
                appSettings = sysData?.appSettings || state.appSettings || {};

                if (cancelled) return;

                // Team-Liste niemals leeren, solange God-Mode aktiv ist
                const liveGod = !!useAdminStore.getState().impersonatedClientId;
                useAppStore.setState({
                    settings: finalSettings,
                    team: (liveGod && (!teamData || teamData.length === 0))
                        ? (state.team || [])
                        : teamData,
                    teamRegistry: (liveGod && (!teamRegistryData || teamRegistryData.length === 0))
                        ? (state.teamRegistry || [])
                        : teamRegistryData,
                    appSettings,
                    isDirty: false,
                    past: [],
                    future: [],
                });
            } catch (e) {
                console.error('Init: Systemdaten konnten nicht geladen werden', e);
                const state = useAppStore.getState();
                teamData = state.team || [];
                teamRegistryData = state.teamRegistry || [];
            }

            if (cancelled) return;

            const roleInfo = resolveTeamMemberByEmail(authUser.email, teamData, teamRegistryData);
            setIsTeamResolutionComplete(true);

            // Während God-Mode: Auth-Bootstrap darf Impersonation nicht anfassen
            const liveGodId = useAdminStore.getState().impersonatedClientId
                || (() => { try { return sessionStorage.getItem('GOD_MODE_ID'); } catch { return null; } })();
            if (liveGodId) {
                // Zustand ggf. wieder an sessionStorage angleichen
                if (!useAdminStore.getState().impersonatedClientId) {
                    useAdminStore.setState({ impersonatedClientId: liveGodId });
                }
                return;
            }

            if (roleInfo.isTeam) {
                // GOD_MODE_ID hier NICHT löschen — sonst Race mit Einloggen-Klick
                localStorage.removeItem('PreisExpert_Data_V2');
                useAppStore.setState({ isInitialized: true, isDirty: false, past: [], future: [], cloudWriteLock: false });
                return;
            }

            // Normaler Mandant — Store laden; Session-Claim erst beim Öffnen der Akte (nicht Dashboard)
            // @ts-ignore
            await initializeStore(authUser.uid);
            if (cancelled) return;
            setSessionBlocked(false);
        })();

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser]);

    // God-Mode: Akte laden wenn Impersonation aktiv und Store noch leer
    // Nur Reload/Login — startImpersonation lädt selbst (godModeInitOwnedByStarter).
    // Paralleler Zweit-Init hat Sync totgemacht (isolateSyncContext Race → FEHLER).
    useEffect(() => {
        if (!authUser || !impersonatedClientId) return;
        if (godModeInitOwnedByStarter) return;
        if (isInitialized && clientData) return;

        let cancelled = false;
        (async () => {
            try {
                // @ts-ignore
                await initializeStore(impersonatedClientId);
            } catch (e) {
                if (cancelled) return;
                console.error('God-Mode Laden fehlgeschlagen:', e);
                try {
                    sessionStorage.setItem('PE_CLOUD_SYNC_BLOCKED', '1');
                    sessionStorage.removeItem('PE_CLOUD_LOAD_OK');
                } catch { /* ignore */ }
                useAppStore.setState((s: any) => ({
                    isInitialized: true,
                    cloudWriteLock: true,
                    clientData: {
                        ...(s.clientData || {}),
                        firmenname: s.clientData?.firmenname || impersonatedClientName || 'God-Mode',
                    },
                }));
            }
        })();

        return () => { cancelled = true; };
    // Nur bei Client-Wechsel neu laden — nicht bei jedem isInitialized-Flip
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser, impersonatedClientId, godModeInitOwnedByStarter]);

    // 🆘 NOTAUSGANG: Hängender God-Mode — nach 12s Init erzwingen (niemals God-Mode abbrechen)
    useEffect(() => {
        if (!impersonatedClientId) return;
        if (isInitialized && clientData) return;

        const timer = window.setTimeout(() => {
            console.error('God-Mode Init Timeout — erzwinge isInitialized mit Fallback-Daten (Sync GESPERRT)');
            try {
                sessionStorage.setItem('PE_CLOUD_SYNC_BLOCKED', '1');
                sessionStorage.removeItem('PE_CLOUD_LOAD_OK');
            } catch { /* ignore */ }
            useAppStore.setState((s: any) => ({
                isInitialized: true,
                cloudWriteLock: true,
                clientData: {
                    ...(s.clientData || {}),
                    firmenname: s.clientData?.firmenname || impersonatedClientName || 'God-Mode',
                    mandantNr: s.clientData?.mandantNr || 'GOD-MODE',
                },
            }));
        }, 12000);

        return () => window.clearTimeout(timer);
    }, [impersonatedClientId, impersonatedClientName, isInitialized, clientData]);

    // Nach Freigabe-Screen: Store + Firestore Unlock heilen, falls Session-Flag gesetzt
    // God-Mode/Impersonation: NIEMALS — sonst sieht Admin alle Module und schreibt Unlock in die Akte
    useEffect(() => {
        if (!authUser || !isInitialized || !clientData) return;
        if (userRoleInfo.isTeam && !impersonatedClientId) return;
        if (impersonatedClientId) return;
        let sessionUnlocked = false;
        try { sessionUnlocked = sessionStorage.getItem('PE_ADMIN_UNLOCKED') === '1'; } catch { /* ignore */ }
        if (!sessionUnlocked) return;
        if (clientData.isUnlockedByAdmin === true) return;

        const targetId = authUser.uid;
        useAppStore.setState((s: any) => ({
            clientData: { ...(s.clientData || {}), isUnlockedByAdmin: true },
        }));
        updateDoc(doc(db, 'kalkulation_mandanten', targetId), {
            isUnlockedByAdmin: true,
            'clientData.isUnlockedByAdmin': true,
        }).catch((e) => console.warn('Unlock-Heal fehlgeschlagen:', e));
    }, [authUser, isInitialized, clientData, userRoleInfo.isTeam, impersonatedClientId]);

    // ── ALL-USER-HEARTBEAT: lastActiveAt (throttled) + Presence-Push 6h-Gate ──
    // Wichtig: KEIN Mount-Ping — sonst meldet ein Hintergrund-Tab / iOS-Resume „Gerade online“
    // + Presence-Push, obwohl niemand aktiv tippt. Nur echte Interaktion / sichtbarer Fokus.
    useEffect(() => {
        // Erst nach Team-Auflösung — sonst schreibt Admin kurz als „Mandant“ und der Throttle blockiert den Team-Write.
        if (!authUser || !isInitialized || !isTeamResolutionComplete) return;

        // Mandant: alle 2 Min (ONLINE-Filter = 20 Min). God-Mode: 30s für AktivCounter.
        const HEARTBEAT_THROTTLE_MS = impersonatedClientId ? 30 * 1000 : 2 * 60 * 1000;
        const SESSION_GATE_MS = 6 * 60 * 60 * 1000; // 6 Stunden → neue Session / Presence-Alert
        const uid = authUser.uid;
        // Impersonation = immer Team-Presence auf users/{uid}, nie Mandanten-Akte
        const writeTeam = userRoleInfo.isTeam || !!impersonatedClientId;
        // Getrennter Throttle-Key je Modus — sonst bleibt der falsche Erst-Write 2 Min „gesperrt“
        const STORAGE_KEY = impersonatedClientId
            ? `pe_last_heartbeat_god_${impersonatedClientId}`
            : writeTeam
              ? `pe_last_heartbeat_team_${uid}`
              : `pe_last_heartbeat_mandant_${uid}`;
        // Cross-Tab-Guard (sessionStorage ist tab-lokal → 2 Tabs = 2 Pushes ohne Lock)
        const PUSH_LOCK_KEY = `pe_presence_alert_lock_${uid}`;
        let inFlight = false;

        const parseLastActiveMs = (raw: any): number | null => {
            if (!raw) return null;
            if (typeof raw.toMillis === 'function') return raw.toMillis();
            if (typeof raw.seconds === 'number') return raw.seconds * 1000;
            if (typeof raw === 'number') return raw;
            const d = new Date(raw);
            const t = d.getTime();
            return Number.isFinite(t) ? t : null;
        };

        const sendHeartbeat = async () => {
            try {
                // Nur bei sichtbarem Tab — Background-Resume ohne Interaktion zählt nicht
                if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

                // PEPulse / Cutier strikt von PEOne-Präsenz trennen (kein Counter, kein Push)
                const pathNow = typeof window !== 'undefined' ? String(window.location.pathname || '').toLowerCase() : '';
                const returnUrl = (() => {
                    try { return String(sessionStorage.getItem('pWA_return_url') || '').toLowerCase(); } catch { return ''; }
                })();
                const standaloneTool = (() => {
                    try { return String(sessionStorage.getItem('pe_standalone_tool') || '').toLowerCase(); } catch { return ''; }
                })();
                const isPepulseContext =
                    pathNow.includes('pepulse') ||
                    returnUrl.includes('pepulse') ||
                    (standaloneTool === 'pepulse' && (pathNow === '/' || pathNow.includes('pepulse')));
                if (isPepulseContext || pathNow.includes('/cutier')) return;

                const lastRaw = sessionStorage.getItem(STORAGE_KEY);
                const last = lastRaw ? Number(lastRaw) : 0;
                if (Date.now() - last < HEARTBEAT_THROTTLE_MS) return;
                if (inFlight) return;
                inFlight = true;
                sessionStorage.setItem(STORAGE_KEY, String(Date.now()));

                // 6h-Gate: vorherigen lastActiveAt lesen → ggf. presence_alert + Session-Zähler
                const activityRef = writeTeam
                    ? doc(db, 'users', uid)
                    : doc(db, 'kalkulation_mandanten', uid);
                let snap: Awaited<ReturnType<typeof getDoc>> | null = null;
                let isNewSession = true;
                try {
                    snap = await getDoc(activityRef);
                    const prevMs = parseLastActiveMs(snap.exists() ? snap.data()?.lastActiveAt : null);
                    isNewSession = prevMs == null || Date.now() - prevMs >= SESSION_GATE_MS;
                    if (isNewSession) {
                        // Idempotenz: feste Doc-ID pro UID+6h-Bucket → 2 Tabs erzeugen keinen 2. Push
                        const bucket = Math.floor(Date.now() / SESSION_GATE_MS);
                        const alertId = `presence_${uid}_${bucket}`;
                        const lockRaw = localStorage.getItem(PUSH_LOCK_KEY);
                        const lockMs = lockRaw ? Number(lockRaw) : 0;
                        const lockFresh = Number.isFinite(lockMs) && Date.now() - lockMs < SESSION_GATE_MS;
                        if (!lockFresh) {
                            localStorage.setItem(PUSH_LOCK_KEY, String(Date.now()));
                            const cd: any = useAppStore.getState().clientData || {};
                            const sourceName = writeTeam
                                ? (authUser.displayName || authUser.email || 'Team')
                                : ([cd.vorname, cd.nachname].filter(Boolean).join(' ') || cd.firmenname || 'Unbekannt');
                            const salonName = writeTeam
                                ? ''
                                : String(cd.firmenname || cd.salonName || cd.salon || '');
                            const sourceRole = writeTeam
                                ? String(userRoleInfo.role || userRoleInfo.category || 'team')
                                : 'mandant';
                            // Feste ID + create-only (Update in Rules verboten):
                            // 2. Tab → permission-denied, kein zweites onCreate
                            await setDoc(doc(db, 'push_queue', alertId), {
                                type: 'presence_alert',
                                sourceUid: uid,
                                sourceName: sourceName || 'Unbekannt',
                                sourceRole,
                                salonName: salonName || '',
                                createdAt: serverTimestamp(),
                                status: 'pending',
                            }).catch((err: any) => {
                                const code = String(err?.code || '');
                                if (code === 'permission-denied' || code === 'already-exists') return;
                                throw err;
                            });
                        }
                    }
                } catch { /* push_queue / read fail silent */ }

                // Impersonation: immer der eingeloggte Admin/Coach (authUser.uid), nie die Mandanten-Akte
                if (writeTeam) {
                    const todayKey = new Date().toISOString().slice(0, 10);
                    const prevDay = snap?.exists() ? String(snap.data()?.activityDay || '') : '';
                    const activityPayload: Record<string, unknown> = {
                        lastActiveAt: serverTimestamp(),
                        activityDay: todayKey,
                        activityPingsToday: prevDay && prevDay !== todayKey ? 1 : increment(1),
                        // Für AktivCounter-Match per E-Mail (Team-ID ≠ Auth-UID)
                        email: authUser.email || snap?.data()?.email || '',
                        displayName: authUser.displayName || snap?.data()?.displayName || '',
                    };
                    if (isNewSession) {
                        activityPayload.activitySessionCount = increment(1);
                        activityPayload.lastSessionAt = serverTimestamp();
                    }
                    setDoc(doc(db, 'users', uid), activityPayload, { merge: true }).catch(() => { /* silent */ });
                } else {
                    // Mandant aktiv → 60-Tage-Mail-Zyklus zurücksetzen (Loop-Reset)
                    updateDoc(doc(db, 'kalkulation_mandanten', uid), {
                        lastActiveAt: serverTimestamp(),
                        inactivityMail60SentAt: deleteField(),
                    }).catch(() => { /* silent */ });
                }
            } catch { /* sessionStorage blocked */ }
            finally {
                inFlight = false;
            }
        };

        const handler = () => { sendHeartbeat(); };
        window.addEventListener('mousemove', handler, { passive: true });
        window.addEventListener('keydown', handler, { passive: true });
        window.addEventListener('pointerdown', handler, { passive: true });
        window.addEventListener('touchstart', handler, { passive: true });
        return () => {
            window.removeEventListener('mousemove', handler);
            window.removeEventListener('keydown', handler);
            window.removeEventListener('pointerdown', handler);
            window.removeEventListener('touchstart', handler);
        };
    }, [authUser, isInitialized, isTeamResolutionComplete, userRoleInfo.isTeam, userRoleInfo.role, userRoleInfo.category, impersonatedClientId]);

    // ── Admin Push: Visibility-Refresh + Badge clear (PEBase-Parität) ──
    useEffect(() => {
        if (!authUser || !isInitialized) return;
        const isAdmin = isAdminPortalAccess(
            userRoleInfo.role,
            userRoleInfo.category,
            authUser.email
        );
        if (!isAdmin) return;

        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            const p = typeof window !== 'undefined' ? String(window.location.pathname || '').toLowerCase() : '';
            const tool = (() => {
                try { return String(sessionStorage.getItem('pe_standalone_tool') || '').toLowerCase(); } catch { return ''; }
            })();
            if (p.includes('pepulse') || tool === 'pepulse') return;
            clearPushBadges().catch(() => {});
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                refreshFcmTokenIfAllowed(authUser.uid, false).catch(() => {});
            }
        };
        onVisible();
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [authUser, isInitialized, userRoleInfo.role, userRoleInfo.category]);

    useEffect(() => {
        if (!authUser || !isInitialized) return;
        if (userRoleInfo.isTeam && !impersonatedClientId) return;

        const safeId = (impersonatedClientId === "undefined" || impersonatedClientId === "null") ? null : impersonatedClientId;
        const targetId = safeId || authUser.uid;
        
        if (!targetId) return;
        
        const docRef = doc(db, 'kalkulation_mandanten', targetId);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                // Wartung nur bei frischem Admin-Heartbeat — sticky Flags ohne HB = freigeben
                const lockSnap = {
                    isLockedByAdmin: data.isLockedByAdmin === true,
                    adminLockHeartbeat: data.adminLockHeartbeat,
                    adminLockHolderUid: data.adminLockHolderUid,
                    adminLockHolderName: data.adminLockHolderName,
                };
                adminLockSnapRef.current = lockSnap;
                const lockLive = impersonatedClientId ? false : isAdminMaintenanceLockLive(lockSnap);
                setIsAdminLocked(lockLive);
                setAdminLockHolderLabel(lockLive ? resolveAdminLockHolderLabel(lockSnap) : null);

                // Lock aktiv: lokalen Dirty-State einfrieren (kein Autosave-Overwrite)
                if (!impersonatedClientId && lockLive) {
                    try {
                        useAppStore.setState({ isDirty: false });
                        (window as any).isSystemDirty = false;
                    } catch { /* ignore */ }
                }

                // Lock aufgehoben: lokalen Altstand verwerfen, frischen Server-Stand laden
                if (!impersonatedClientId && wasAdminLockedRef.current && !lockLive) {
                    try {
                        useAppStore.setState({
                            isDirty: false,
                            cloudWriteLock: true,
                            past: [],
                            future: [],
                        });
                        (window as any).isSystemDirty = false;
                        try {
                            sessionStorage.setItem('PE_CLOUD_SYNC_BLOCKED', '1');
                            sessionStorage.removeItem('PE_CLOUD_LOAD_OK');
                        } catch { /* ignore */ }
                        const init = useAppStore.getState().initializeStore;
                        if (typeof init === 'function') {
                            void Promise.resolve(init(targetId))
                                .catch((e) => console.warn('Post-Lock Flush fehlgeschlagen:', e))
                                .finally(() => {
                                    // Nur freigeben wenn Cloud-Load wirklich OK war —
                                    // sonst bleiben BLOCKED + WriteLock (kein Gift-Sync).
                                    let loadOk = false;
                                    try { loadOk = sessionStorage.getItem('PE_CLOUD_LOAD_OK') === '1'; } catch { /* ignore */ }
                                    if (loadOk) {
                                        try { sessionStorage.removeItem('PE_CLOUD_SYNC_BLOCKED'); } catch { /* ignore */ }
                                        try { useAppStore.setState({ cloudWriteLock: false, isDirty: false }); } catch { /* ignore */ }
                                    }
                                });
                        } else {
                            try { sessionStorage.removeItem('PE_CLOUD_SYNC_BLOCKED'); } catch { /* ignore */ }
                            useAppStore.setState({ cloudWriteLock: false });
                        }
                    } catch (e) {
                        console.warn('Post-Lock Flush Setup fehlgeschlagen:', e);
                    }
                }
                if (!impersonatedClientId) {
                    wasAdminLockedRef.current = lockLive;
                }

                // Hängengebliebener Legacy-Lock (Flag ohne Heartbeat / abgelaufen): einmal heilen
                if (
                    !impersonatedClientId &&
                    data.isLockedByAdmin === true &&
                    !lockLive &&
                    staleAdminLockHealRef.current !== targetId
                ) {
                    staleAdminLockHealRef.current = targetId;
                    updateDoc(docRef, buildAdminLockPayload(false) as any).catch((e) =>
                        console.warn('Stale Admin-Lock Heal fehlgeschlagen:', e)
                    );
                }

                const years = data.clientData?.geschaeftsjahre || data.geschaeftsjahre || [];
                setMasterJahre(years);

                // Live Session-Lock: nur in der AKTE blocken — Dashboard bleibt für beide frei
                if (authUser) {
                    const localSessionId = getOrCreateLocalSessionId();
                    const isGod = !!impersonatedClientId;
                    const claimOpts = { userId: authUser.uid, isGodMode: isGod, claimantIsTeam: isGod };
                    const active = data.activeSession || null;
                    const weOwnSession = !!(active?.sessionId && active.sessionId === localSessionId);
                    const onDashboard = currentPageRef.current === 'jahresuebersicht';
                    const foreignLive = isForeignSessionBlocking(active, authUser.uid, localSessionId, claimOpts);

                    if (weOwnSession) {
                        // Inhaber: nie Modal, nie Rauswurf
                        setSessionBlocked(false);
                        setSessionCanResume(false);
                        setSessionBlockHolderLabel(null);
                    } else if (onDashboard) {
                        // Jahresübersicht: fremde Session ignorieren (außer Claim-Versuch hat schon geblockt)
                        if (useAdminStore.getState().isSessionBlocked) {
                            if (!foreignLive) {
                                setSessionCanResume(true);
                            } else {
                                setSessionCanResume(false);
                                setSessionBlockHolderLabel(labelFromActiveSession(active));
                            }
                        }
                    } else if (foreignLive) {
                        // In der Akte und fremde Person / anderer Coach → Wartemodal (+ Name)
                        setSessionBlocked(true);
                        setSessionCanResume(false);
                        setSessionBlockHolderLabel(labelFromActiveSession(active));
                    } else {
                        // Kein Fremd-Lock: eigene Stale-Session nach Reload → einmal reclaimen (kein Write-Loop)
                        const needsReclaim =
                            !!active?.sessionId || useAdminStore.getState().isSessionBlocked;
                        if (needsReclaim && !sessionReclaimInFlightRef.current) {
                            sessionReclaimInFlightRef.current = true;
                            void tryClaimActiveSession(targetId, {
                                ...claimOpts,
                                holderName: isGod ? resolveSessionHolderName() : undefined,
                            })
                                .then((claim) => {
                                    if (claim.ok) {
                                        setSessionBlocked(false);
                                        setSessionCanResume(false);
                                        setSessionBlockHolderLabel(null);
                                    } else if (useAdminStore.getState().isSessionBlocked) {
                                        setSessionCanResume(true);
                                    }
                                })
                                .finally(() => {
                                    sessionReclaimInFlightRef.current = false;
                                });
                        } else if (!needsReclaim) {
                            setSessionBlocked(false);
                            setSessionCanResume(false);
                            setSessionBlockHolderLabel(null);
                        }
                    }
                }
            }
        }, (error) => {
            console.error("FATAL: App.tsx Snapshot-Listener fehlgeschlagen. Loop verhindert:", error);
        });

        return () => unsubscribe();
    }, [authUser, isInitialized, impersonatedClientId, userRoleInfo.isTeam, setSessionBlocked, labelFromActiveSession, resolveSessionHolderName]);

    // Wartungs-Overlay lokal ablaufen lassen (auch ohne neuen Snapshot)
    useEffect(() => {
        const reeval = () => {
            if (impersonatedClientId) {
                setIsAdminLocked(false);
                setAdminLockHolderLabel(null);
                return;
            }
            const snap = adminLockSnapRef.current;
            const live = isAdminMaintenanceLockLive(snap);
            setIsAdminLocked((prev) => (prev === live ? prev : live));
            setAdminLockHolderLabel(live ? resolveAdminLockHolderLabel(snap) : null);
        };
        reeval();
        const intervalId = window.setInterval(reeval, 10_000);
        return () => window.clearInterval(intervalId);
    }, [impersonatedClientId]);

    // God-Mode: Lock nur bei sichtbarem Tab + echter Aktivität — sonst TTL (~3 Min) / Idle (5 Min)
    useEffect(() => {
        if (!impersonatedClientId || !authUser) return;
        const clientId = impersonatedClientId;
        godModeLastActivityRef.current = Date.now();
        let releasedForIdle = false;

        const resolveHolder = (): AdminLockHolder => {
            const email = String(authUser.email || '').trim().toLowerCase();
            const pool = [...(team || []), ...(teamRegistry || [])];
            const member =
                pool.find((m: any) => String(m?.id || '').trim() === authUser.uid) ||
                pool.find((m: any) => String(m?.email || '').trim().toLowerCase() === email) ||
                null;
            const name =
                String(member?.name || '').trim() ||
                String(authUser.displayName || '').trim() ||
                (email ? email.split('@')[0] : '') ||
                'PreisExpert-Team';
            return { uid: authUser.uid, name, email: email || undefined };
        };

        const markActive = () => {
            godModeLastActivityRef.current = Date.now();
            // Nach Idle-Freigabe: bei neuer Aktivität Lock wieder setzen (nur sichtbarer Tab)
            if (releasedForIdle && document.visibilityState === 'visible') {
                releasedForIdle = false;
                if (useAdminStore.getState().impersonatedClientId === clientId) {
                    void heartbeatAdminMaintenanceLock(clientId, resolveHolder());
                }
            }
        };

        const activityEvents: Array<keyof WindowEventMap> = [
            'mousemove',
            'mousedown',
            'keydown',
            'touchstart',
            'scroll',
            'wheel',
            'pointerdown',
        ];
        activityEvents.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                markActive();
            }
            // KEIN Lock-Release bei Tab-Hide — Hard-Lock bleibt bis BEENDEN / Idle / TTL
        };
        document.addEventListener('visibilitychange', onVisibility);

        const tick = () => {
            if (useAdminStore.getState().impersonatedClientId !== clientId) return;
            // Unsichtbarer Tab: kein Heartbeat (TTL bzw. visibilitychange gibt frei)
            if (document.visibilityState !== 'visible') return;

            const idleFor = Date.now() - godModeLastActivityRef.current;
            if (idleFor >= ADMIN_LOCK_IDLE_MS) {
                if (!releasedForIdle) {
                    releasedForIdle = true;
                    void releaseAdminMaintenanceLock(clientId);
                    void clearOwnedActiveSession(clientId, { matchUserId: authUser.uid });
                }
                return;
            }
            releasedForIdle = false;
            void heartbeatAdminMaintenanceLock(clientId, resolveHolder());
        };
        tick();
        const intervalId = window.setInterval(tick, ADMIN_LOCK_HEARTBEAT_MS);
        return () => {
            window.clearInterval(intervalId);
            activityEvents.forEach((evt) => window.removeEventListener(evt, markActive));
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [impersonatedClientId, authUser, team, teamRegistry]);

    // God-Mode Tab schließen: Lock best-effort freigeben (TTL fängt Restfälle nach ~3 Min.)
    useEffect(() => {
        if (!impersonatedClientId) return;
        const clientId = impersonatedClientId;
        const releaseLock = () => {
            // Nur freigeben wenn dieser Tab noch der God-Mode-Holder war
            try {
                const liveId = useAdminStore.getState().impersonatedClientId
                    || sessionStorage.getItem('GOD_MODE_ID');
                if (liveId !== clientId) return;
            } catch { /* ignore */ }
            try {
                void adminUpdateMandantRoot(clientId, buildAdminLockPayload(false) as any);
            } catch { /* ignore */ }
            try {
                void clearOwnedActiveSession(clientId, {
                    matchUserId: auth.currentUser?.uid || undefined,
                });
            } catch { /* ignore */ }
        };
        const onPageHide = () => releaseLock();
        window.addEventListener('pagehide', onPageHide);
        return () => {
            window.removeEventListener('pagehide', onPageHide);
        };
    }, [impersonatedClientId]);

    useEffect(() => {
        const syncState = () => {
            let activeYear = localStorage.getItem('CURRENT_YEAR') || '';
            // Stale localStorage nie als Wahrheit für Lock — erst yearPool klären
            let activeStatus = 'datenerfassung';

            // Store hat Vorrang vor Snapshot (sonst hängt Status auf altem masterJahre fest)
            const storeJahre = useAppStore.getState().clientData?.geschaeftsjahre || [];
            const yearPool =
                (Array.isArray(storeJahre) && storeJahre.length > 0)
                    ? storeJahre
                    : (masterJahre && masterJahre.length > 0 ? masterJahre : []);

            if (!activeYear && yearPool.length > 0) {
                activeYear = String(Math.max(...yearPool.map((j: any) => Number(j.jahr) || 0)));
            }
            if (!activeYear) {
                activeYear = new Date().getFullYear().toString();
            }

            if (yearPool.length > 0) {
                let exactYearObj = yearPool.find((j: any) => String(j.jahr) === String(activeYear));
                if (!exactYearObj) {
                    // Stale CURRENT_YEAR (z. B. Kalenderjahr) → auf Pool-Jahr zurücksetzen
                    activeYear = String(Math.max(...yearPool.map((j: any) => Number(j.jahr) || 0)));
                    exactYearObj = yearPool.find((j: any) => String(j.jahr) === String(activeYear));
                }
                if (exactYearObj) {
                    // Quelle der Wahrheit: Jahr-Objekt. Fehlender Status = offen (kein Abschluss).
                    const raw = exactYearObj.status;
                    activeStatus = (raw && String(raw).trim()) ? String(raw).trim() : 'datenerfassung';
                } else {
                    const ls = localStorage.getItem('CURRENT_YEAR_STATUS');
                    activeStatus = (ls && ls !== 'abgeschlossen') ? ls : 'datenerfassung';
                }
            } else {
                // Kein Pool: Stale-Lock nicht halten
                const ls = localStorage.getItem('CURRENT_YEAR_STATUS');
                activeStatus = (ls && ls !== 'abgeschlossen') ? ls : 'datenerfassung';
            }

            if (localStorage.getItem('CURRENT_YEAR') !== activeYear) {
                localStorage.setItem('CURRENT_YEAR', activeYear);
            }
            if (localStorage.getItem('CURRENT_YEAR_STATUS') !== activeStatus) {
                localStorage.setItem('CURRENT_YEAR_STATUS', activeStatus);
            }
            try { sessionStorage.setItem('CURRENT_YEAR', activeYear); } catch (_) { /* ignore */ }

            setCurrentYear(activeYear);
            setCurrentYearStatus(activeStatus);
            // Nur explizites 'abgeschlossen' sperrt — aktiv/datenerfassung/angefragt = offen
            strokeIsYearLocked(activeStatus === 'abgeschlossen');
        };

        syncState();
        const intervalId = setInterval(syncState, 200); 
        window.addEventListener('storage', syncState);
        const onPeYearStatus = () => syncState();
        window.addEventListener('pe-year-status', onPeYearStatus as EventListener);

        return () => {
            window.removeEventListener('storage', syncState);
            window.removeEventListener('pe-year-status', onPeYearStatus as EventListener);
            clearInterval(intervalId);
        };
    }, [masterJahre, clientData?.geschaeftsjahre]); 

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if ((window as any).isSystemDirty) {
                e.preventDefault();
                e.returnValue = 'Achtung! Daten werden noch im Hintergrund gespeichert.';
                return 'Achtung! Daten werden noch im Hintergrund gespeichert.';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    // 🛡️ ONBOARDING-ROUTE-GUARD: Nicht freigeschaltete Mandanten dürfen NUR /onboarding sehen.
    // Fängt insbesondere den Browser-Zurück-Button (popstate) ab, der keinen Re-Render auslöst
    // und daher die Render-Schranke weiter unten umgehen würde.
    // Doppel-Schranke PEBase↔P1: ohne isUnlockedByAdmin === true kein Dashboard / keine Kern-App.
    useEffect(() => {
        const enforceOnboardingLock = () => {
            if (userRoleInfo.isTeam) return; // Admin/Coach & Shadow-Mode niemals blockieren
            if (useAdminStore.getState().impersonatedClientId) return;
            if (useAdminStore.getState().isSessionBlocked) return; // Session-Lock: kein Onboarding-Redirect
            try { if (sessionStorage.getItem('GOD_MODE_ID')) return; } catch { /* ignore */ }
            // Warten bis Init fertig — sonst Redirect mit leerem/teilweisem State
            if (!isInitialized) return;
            const sessionUnlocked = (() => {
                try { return sessionStorage.getItem('PE_ADMIN_UNLOCKED') === '1'; } catch { return false; }
            })();
            // Hart: nur explizit freigeschaltet (=== true) darf in die Kern-App
            if (clientData?.isUnlockedByAdmin === true || sessionUnlocked) return;
            if (window.location.pathname.includes('/onboarding')) return;

            // Geister-Akten-Schutz: Keine veralteten lokalen Daten nach dem Redirect hydrieren
            localStorage.removeItem('PreisExpert_Data_V2');
            // .replace statt .href: kein neuer History-Eintrag, kein Zurück-Ping-Pong
            window.location.replace('/onboarding');
        };

        enforceOnboardingLock();
        window.addEventListener('popstate', enforceOnboardingLock);
        return () => window.removeEventListener('popstate', enforceOnboardingLock);
    }, [userRoleInfo.isTeam, clientData, isSessionBlocked, isInitialized]);

    useEffect(() => {
        if (!isInitialized || !authUser) return;
        if (userRoleInfo.isTeam && !impersonatedClientId) return;
        if (isSessionBlocked) return;
        // Mandant unter God-Mode-Lock: Autosave komplett einfrieren (Coach selbst speichert weiter)
        if (isAdminLocked && !impersonatedClientId) return;

        let lastClientDataStr = JSON.stringify(useAppStore.getState().clientData);
        
        const unsubscribe = useAppStore.subscribe((state: any) => {
            if (!state.isInitialized || state.cloudWriteLock) {
                cancelPendingAutosync();
                return;
            }
            try {
                if (sessionStorage.getItem('PE_CLOUD_SYNC_BLOCKED') === '1') {
                    cancelPendingAutosync();
                    return;
                }
                if (sessionStorage.getItem('PE_CLOUD_LOAD_OK') !== '1') {
                    cancelPendingAutosync();
                    return;
                }
            } catch { /* ignore */ }
            // Nur Mandant blocken — Coach im God-Mode hält selbst den Lock und MUSS speichern dürfen
            const inGodMode = !!useAdminStore.getState().impersonatedClientId
                || (() => { try { return !!sessionStorage.getItem('GOD_MODE_ID'); } catch { return false; } })();
            if (!inGodMode && isAdminMaintenanceLockLive(adminLockSnapRef.current)) {
                cancelPendingAutosync();
                return;
            }

            const currentStr = JSON.stringify(state.clientData);
            
            if (currentStr !== lastClientDataStr) {
                lastClientDataStr = currentStr;
                (window as any).isSystemDirty = true;
                
                const debounceDelay = !!sessionStorage.getItem('GOD_MODE_ID') ? 3000 : 2000;
                const epochAtSchedule = getSyncEpoch();

                scheduleAutosync(async () => {
                    if (!useAppStore.getState().isInitialized || useAppStore.getState().cloudWriteLock) {
                        return;
                    }
                    if (epochAtSchedule !== getSyncEpoch()) {
                        return;
                    }
                    try {
                        if (sessionStorage.getItem('PE_CLOUD_SYNC_BLOCKED') === '1') return;
                        if (sessionStorage.getItem('PE_CLOUD_LOAD_OK') !== '1') return;
                    } catch { /* ignore */ }
                    if (useAdminStore.getState().isSessionBlocked) {
                        return;
                    }
                    const stillGod = !!useAdminStore.getState().impersonatedClientId
                        || (() => { try { return !!sessionStorage.getItem('GOD_MODE_ID'); } catch { return false; } })();
                    if (!stillGod && isAdminMaintenanceLockLive(adminLockSnapRef.current)) {
                        return;
                    }
                    setSyncStatus('syncing');
                    try {
                        const success = await migrateLocalStateToCloud();
                        if (success) {
                            (window as any).isSystemDirty = false;
                            useAppStore.setState({ isDirty: false });
                            setSyncStatus('success');
                            setTimeout(() => setSyncStatus('idle'), 2000);
                        } else {
                            // Soft-Block (Load/Session/Lock) ≠ roter Dauer-Fehler; Retry beim nächsten Edit
                            const softBlocked = (() => {
                                try {
                                    if (
                                        sessionStorage.getItem('PE_CLOUD_SYNC_BLOCKED') === '1'
                                        || sessionStorage.getItem('PE_CLOUD_LOAD_OK') !== '1'
                                    ) {
                                        return true;
                                    }
                                } catch { /* ignore */ }
                                try {
                                    const st = useAppStore.getState();
                                    if (st.cloudWriteLock || !st.isInitialized) return true;
                                } catch { /* ignore */ }
                                return false;
                            })();
                            if (softBlocked) {
                                setSyncStatus('idle');
                            } else {
                                setSyncStatus('error');
                            }
                        }
                    } catch(e) {
                        console.error('Autosync fehlgeschlagen:', e);
                        setSyncStatus('error');
                    }
                }, debounceDelay, epochAtSchedule);
            }
        });
        return () => { unsubscribe(); cancelPendingAutosync(); };
    }, [isInitialized, authUser, userRoleInfo.isTeam, impersonatedClientId, isSessionBlocked, isAdminLocked]);

    // Session-Lock Heartbeat: nur in der Akte, nur eigene Session (siehe heartbeatActiveSession)
    useEffect(() => {
        if (!authUser || isSessionBlocked) return;
        if (userRoleInfo.isTeam && !impersonatedClientId) return;
        if (currentPage === 'jahresuebersicht') return;

        const targetId = impersonatedClientId || authUser.uid;
        if (!targetId) return;
        const isGod = !!impersonatedClientId;

        const tick = () => {
            // God-Mode idle: Session nicht erneuern — Mandant darf übernehmen
            if (isGod && Date.now() - godModeLastActivityRef.current >= ADMIN_LOCK_IDLE_MS) {
                return;
            }
            void heartbeatActiveSession(targetId, authUser.uid, {
                holderIsTeam: isGod,
                holderName: isGod ? resolveSessionHolderName() : undefined,
            });
        };
        tick();
        const intervalId = window.setInterval(tick, SESSION_HEARTBEAT_MS);
        return () => window.clearInterval(intervalId);
    }, [authUser, isSessionBlocked, userRoleInfo.isTeam, impersonatedClientId, currentPage, resolveSessionHolderName]);

    useEffect(() => {
        if (!isInitialized || !clientData) return;

        const effectiveUnlocked: string[] = [];
        for (const list of [unlockedPremiumTools, (clientData as any)?.unlockedPremiumTools]) {
            if (!Array.isArray(list)) continue;
            for (const id of list) {
                if (typeof id === 'string' && id && !effectiveUnlocked.includes(id)) {
                    effectiveUnlocked.push(id);
                }
            }
        }

        const wantsPePapers =
            currentPage === 'pe-papers' || window.location.pathname.includes('/pe-papers');
        const isPePapersUnlocked = effectiveUnlocked.includes('pe-papers');
        const wantsPeProfit =
            currentPage === 'pe-profit' || window.location.pathname.includes('/pe-profit');
        const isPeProfitUnlocked = effectiveUnlocked.includes('pe-profit');
        const wantsPeStart =
            currentPage === 'pe-start' || window.location.pathname.includes('/pe-start');
        const isPeStartUnlocked = effectiveUnlocked.includes('pe-start');
        const wantsPeRent =
            currentPage === 'pe-rent' || window.location.pathname.includes('/pe-rent');
        const isPeRentUnlocked = effectiveUnlocked.includes('pe-rent');
        const wantsPeScout =
            currentPage === 'pe-scout' || window.location.pathname.includes('/pe-scout');
        const isPeScoutUnlocked = effectiveUnlocked.includes('pe-scout');
        const wantsPeFacts =
            currentPage === 'pe-facts' || window.location.pathname.includes('/pe-facts');
        const isPeFactsUnlocked = effectiveUnlocked.includes('pe-facts');
        const isGodModeActive =
            Boolean(impersonatedClientId) ||
            Boolean((clientData as any)?._isGodMode) ||
            (typeof sessionStorage !== 'undefined' && Boolean(sessionStorage.getItem('GOD_MODE_ID')));

        const wantsPlanungsrechner =
            currentPage === 'planungsrechner' ||
            window.location.pathname.includes('/planungsrechner');
        if (wantsPlanungsrechner && !isGodModeActive) {
            if (window.location.pathname.includes('/planungsrechner')) {
                window.history.replaceState({}, '', '/');
            }
            if (currentPage === 'planungsrechner') {
                setCurrentPage('startseite');
            }
            return;
        }

        const wantsPeSolo =
            currentPage === 'pe-solo' || window.location.pathname.includes('/pe-solo');
        if (wantsPeSolo && !isGodModeActive) {
            if (window.location.pathname.includes('/pe-solo')) {
                window.history.replaceState({}, '', '/');
            }
            if (currentPage === 'pe-solo') {
                setCurrentPage('startseite');
            }
            return;
        }

        const wantsPePlan =
            currentPage === 'pe-plan' || window.location.pathname.includes('/pe-plan');
        const isPePlanUnlocked = effectiveUnlocked.includes('pe-plan');

        if (wantsPePapers && !isPePapersUnlocked) {
            if (window.location.pathname.includes('/pe-papers')) {
                window.history.replaceState({}, '', '/');
            }
            if (currentPage === 'pe-papers') {
                setCurrentPage('startseite');
            }
            openPremiumModal('pe-papers');
            return;
        }

        if (wantsPeProfit && !isPeProfitUnlocked) {
            if (window.location.pathname.includes('/pe-profit')) {
                window.history.replaceState({}, '', '/');
            }
            if (currentPage === 'pe-profit') {
                setCurrentPage('startseite');
            }
            openPremiumModal('pe-profit');
            return;
        }

        if (wantsPeStart && !isPeStartUnlocked) {
            if (window.location.pathname.includes('/pe-start')) {
                window.history.replaceState({}, '', '/');
            }
            if (currentPage === 'pe-start') {
                setCurrentPage('startseite');
            }
            openPremiumModal('pe-start');
            return;
        }

        if (wantsPeRent && !isPeRentUnlocked) {
            if (window.location.pathname.includes('/pe-rent')) {
                window.history.replaceState({}, '', '/');
            }
            if (currentPage === 'pe-rent') {
                setCurrentPage('startseite');
            }
            openPremiumModal('pe-rent');
            return;
        }

        if (wantsPeScout && !isPeScoutUnlocked) {
            if (window.location.pathname.includes('/pe-scout')) {
                window.history.replaceState({}, '', '/');
            }
            if (currentPage === 'pe-scout') {
                setCurrentPage('startseite');
            }
            openPremiumModal('pe-scout');
            return;
        }

        if (wantsPeFacts && !isPeFactsUnlocked) {
            if (window.location.pathname.includes('/pe-facts')) {
                window.history.replaceState({}, '', '/');
            }
            if (currentPage === 'pe-facts') {
                setCurrentPage('startseite');
            }
            openPremiumModal('pe-facts');
            return;
        }

        if (wantsPePlan && !isPePlanUnlocked) {
            if (window.location.pathname.includes('/pe-plan')) {
                window.history.replaceState({}, '', '/');
            }
            if (currentPage === 'pe-plan') {
                setCurrentPage('startseite');
            }
            openPremiumModal('pe-plan');
        }
    }, [isInitialized, clientData, currentPage, unlockedPremiumTools, openPremiumModal, impersonatedClientId]);

    const showNotification = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
        setNotification({ msg, type });
    }, []);

    /** Navigation mit Session-Lock: Claim erst beim Verlassen der Jahresübersicht → Akte */
    const navigateToPage = useCallback((page: string) => {
        const go = async () => {
            if (page === 'jahresuebersicht') {
                const targetId = impersonatedClientId || authUser?.uid;
                if (targetId) {
                    void clearOwnedActiveSession(targetId);
                }
                setSessionBlocked(false);
                setSessionCanResume(false);
                setCurrentPage('jahresuebersicht');
                return;
            }

            // Schon in der Akte → freie Navigation zwischen Modulen
            if (currentPageRef.current !== 'jahresuebersicht') {
                setCurrentPage(page);
                return;
            }

            // Vom Dashboard in die Akte: jetzt claimen
            if (!authUser) {
                setCurrentPage(page);
                return;
            }
            if (userRoleInfo.isTeam && !impersonatedClientId) {
                setCurrentPage(page);
                return;
            }

            const targetId = impersonatedClientId || authUser.uid;
            const isGod = !!impersonatedClientId;
            const claimOpts = {
                userId: authUser.uid,
                isGodMode: isGod,
                claimantIsTeam: isGod,
                holderName: isGod ? resolveSessionHolderName() : undefined,
            };
            const claim = await tryClaimActiveSession(targetId, claimOpts);
            if (!claim.ok) {
                // Nur blocken wenn wirklich eine fremde Session aktiv ist —
                // verhindert Modal-Flash bei transienten Claim-Fehlern
                const stillForeign = await checkRemoteSessionBlocking(targetId, claimOpts);
                if (stillForeign) {
                    try {
                        const snap = await getDoc(doc(db, 'kalkulation_mandanten', targetId));
                        const active = snap.exists()
                            ? ((snap.data()?.activeSession || null) as Partial<ActiveSession> | null)
                            : null;
                        setSessionBlockHolderLabel(labelFromActiveSession(active));
                    } catch {
                        setSessionBlockHolderLabel(null);
                    }
                    setSessionBlocked(true);
                    setSessionCanResume(false);
                    return;
                }
            }
            setSessionBlocked(false);
            setSessionCanResume(false);
            setSessionBlockHolderLabel(null);
            setCurrentPage(page);
        };
        void go();
    }, [authUser, impersonatedClientId, userRoleInfo.isTeam, setSessionBlocked, resolveSessionHolderName, labelFromActiveSession]);

    const allPages = useMemo(() => {
        const enhancedClientData = clientData
            ? {
                  ...clientData,
                  _isGodMode: isShadowing,
                  _forcedYearStatus: currentYearStatus,
                  unlockedPremiumTools:
                      unlockedPremiumTools.length > 0
                          ? unlockedPremiumTools
                          : clientData.unlockedPremiumTools,
              }
            : clientData;
        // @ts-ignore
        return (getAppPages(enhancedClientData, settings, navigateToPage) || []) as any[];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientData, settings, currentYear, currentYearStatus, isShadowing, navigateToPage, unlockedPremiumTools]);

    const currentPageConfig = allPages.find((p: any) => p.key === currentPage);
    const PageComponent = currentPageConfig?.component;
    const contentLayoutClass = currentPageConfig?.layoutWidth || 'max-w-7xl';
    
    // God-Mode: nie Panzerglas — Admin muss in der Akte schreiben können
    const effectiveIsYearLocked = isYearLocked && !isShadowing;
    
    const isBasic = clientData?.lizenzDetails?.typ === 'basic' || clientData?.basicChecked;
    const logoSrc = isBasic ? (appSettings.basicLogoUrl || appSettings.logoUrl) : appSettings.logoUrl;

    const executeStopImpersonation = () => {
        stopImpersonation(); 
        localStorage.removeItem('CURRENT_YEAR_STATUS');
        setCurrentPage('jahresuebersicht');
        setExitWarningModal({ isOpen: false, action: null, reason: null });
    };

    const confirmExitAction = () => {
        if (exitWarningModal.action === 'logout') {
            setExitWarningModal({ isOpen: false, action: null, reason: null });
            // Gott-Modus: Coach speichert nicht — kein PC-Safe
            if (isShadowing) {
                finishGlobalLogout();
                return;
            }
            setPcSafeLogoutOpen(true);
            return;
        }
        if (exitWarningModal.action === 'stop_impersonation') {
            executeStopImpersonation();
        }
        setExitWarningModal({ isOpen: false, action: null, reason: null });
    };

    /** dirty = ungespeicherte Syncs; year_open = Jahr noch in Erfassung (nur Mandant). */
    const resolveExitWarning = (): 'dirty' | 'year_open' | null => {
        try {
            if ((window as any).isSystemDirty) return 'dirty';
            if (useAppStore.getState().isDirty) return 'dirty';
            if (syncStatus === 'syncing') return 'dirty';
        } catch { /* ignore */ }

        // Gott-Modus: Jahr-Status ist Sache des Mandanten — kein „Datenerfassung“-Schrecken
        if (isShadowing) return null;

        const activeYear = localStorage.getItem('CURRENT_YEAR');
        if (!activeYear || !masterJahre || masterJahre.length === 0) return null;

        const activeYearData = masterJahre.find((j: any) => String(j.jahr) === activeYear);
        if (!activeYearData) return null;

        const st = String(activeYearData.status || '').toLowerCase();
        if (st === 'angefragt' || st === 'datenerfassung') return 'year_open';
        return null;
    };

    const toggleTheme = useCallback(() => {
        const root = document.documentElement;
        const nextIsDark = !root.classList.contains('dark');
        if (nextIsDark) {
            root.classList.add('dark');
            localStorage.setItem('pe-theme', 'dark');
        } else {
            root.classList.remove('dark');
            localStorage.setItem('pe-theme', 'light');
        }
        setIsDarkTheme(nextIsDark);
    }, []);

    const finishGlobalLogout = () => {
        const sessionTargetId = impersonatedClientId || authUser?.uid;
        if (sessionTargetId) {
            void clearOwnedActiveSession(sessionTargetId);
        }
        try { sessionStorage.removeItem('GOD_MODE_ID'); } catch (_) { /* ignore */ }
        useAdminStore.setState({ impersonatedClientId: null, impersonatedClientName: null });
        if (isShadowing) {
            stopImpersonation();
        }
        setSessionBlocked(false);
        setPcSafeLogoutOpen(false);
        signOut(auth);
    };

    const handleGlobalLogout = () => {
        // Gott-Modus (Coach): kein PC-Safe — Coach speichert keine Mandanten-Daten
        if (isShadowing) {
            const warn = resolveExitWarning();
            if (warn) {
                setExitWarningModal({ isOpen: true, action: 'logout', reason: warn });
                return;
            }
            finishGlobalLogout();
            return;
        }
        setPcSafeLogoutOpen(true);
    };
    
    const handleStopImpersonation = () => { 
        const warn = resolveExitWarning();
        if (warn) {
            setExitWarningModal({ isOpen: true, action: 'stop_impersonation', reason: warn });
            return;
        }
        executeStopImpersonation();
    };

    // Emergency-Cloud-Push (UI-Hook aktuell unverdrahtet — absichtlich behalten)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleManualForceSave = async () => {
        setSyncStatus('syncing');
        try {
            const success = await migrateLocalStateToCloud();
            if (success) {
                (window as any).isSystemDirty = false; 
                useAppStore.setState({ isDirty: false });
                setSyncStatus('success');
                alert('🚑 ERFOLG! Das lokale Backup wurde jetzt zwingend in Firebase gespeichert.');
                setTimeout(() => setSyncStatus('idle'), 3000);
            } else { setSyncStatus('error'); alert('🔴 FEHLER: Firebase blockiert.'); }
        } catch (error) { setSyncStatus('error'); alert('🔴 FEHLER beim Cloud-Push.'); }
    };

    const handleClientDownload = () => {
        setPcSafeManualOpen(true);
    };

    // Öffentliche Routen (/seminar, /simplie-kalkulator, /unsubscribe, /einladung)
    // werden AUSSCHLIESSLICH in index.tsx → PublicAppShell geroutet — nicht hier.

    const handleSessionResume = async () => {
        if (!authUser || isSessionResumePending) return;
        const targetId = impersonatedClientId || authUser.uid;
        if (!targetId) return;

        setIsSessionResumePending(true);
        try {
            const isGod = !!impersonatedClientId;
            const claim = await tryClaimActiveSession(targetId, {
                userId: authUser.uid,
                isGodMode: isGod,
                claimantIsTeam: isGod,
                holderName: isGod ? resolveSessionHolderName() : undefined,
            });
            if (claim.ok) {
                setSessionBlocked(false);
                setSessionCanResume(false);
                setSessionBlockHolderLabel(null);
            } else {
                setSessionCanResume(false);
                alert(
                    impersonatedClientId
                        ? sessionBlockHolderLabel
                            ? `${sessionBlockHolderLabel} ist noch in dieser Akte aktiv.`
                            : 'Ein anderer Coach/Admin ist noch in dieser Akte aktiv.'
                        : 'Das andere Gerät ist noch aktiv. Bitte weiter warten.'
                );
            }
        } finally {
            setIsSessionResumePending(false);
        }
    };

    const sessionBlockadeModal = (
        <AnimatePresence>
            {isSessionBlocked && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100000] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center text-center p-6"
                >
                    <div className="w-24 h-24 bg-[#E6C299]/10 rounded-full flex items-center justify-center mb-6 border border-[#E6C299]/30">
                        <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2 }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#E6C299" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </motion.div>
                    </div>
                    <p className="text-slate-200 max-w-lg text-lg leading-relaxed">
                        {impersonatedClientId
                            ? sessionBlockHolderLabel
                                ? <>Achtung: <strong className="text-[#E6C299]">{sessionBlockHolderLabel}</strong> ist aktuell in dieser Akte aktiv. Gleichzeitige Bearbeitung durch zwei Coaches ist gesperrt, um Datenkonflikte zu vermeiden.</>
                                : 'Achtung: Ein anderer Coach/Admin ist aktuell in dieser Akte aktiv. Gleichzeitige Bearbeitung durch zwei Coaches ist gesperrt, um Datenkonflikte zu vermeiden.'
                            : 'Bearbeiten nicht möglich, das Programm ist auf einem anderen Gerät geöffnet, bitte rausgehen und warten, bis das andere Gerät seine Arbeit erledigt hat!'}
                    </p>
                    {sessionBlockHolderLabel && impersonatedClientId && (
                        <p className="mt-4 text-[#E6C299] font-black uppercase tracking-widest text-sm">
                            Aktuell in der Akte: {sessionBlockHolderLabel}
                        </p>
                    )}
                    {sessionCanResume ? (
                        <button
                            type="button"
                            onClick={() => { void handleSessionResume(); }}
                            disabled={isSessionResumePending}
                            className="mt-8 px-6 py-3 bg-[#E6C299] hover:bg-white text-black font-black uppercase tracking-widest text-xs rounded-xl disabled:opacity-60"
                        >
                            {isSessionResumePending ? 'Wird geprüft…' : 'Jetzt weiterarbeiten'}
                        </button>
                    ) : (
                        <p className="mt-6 text-slate-500 text-xs uppercase tracking-widest">
                            {impersonatedClientId
                                ? sessionBlockHolderLabel
                                    ? `Warte, bis ${sessionBlockHolderLabel} die Akte verlässt…`
                                    : 'Warte, bis der andere Coach/Admin die Akte verlässt…'
                                : 'Warte auf Freigabe des anderen Geräts…'}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={() => {
                            setSessionBlocked(false);
                            setSessionCanResume(false);
                            setSessionBlockHolderLabel(null);
                            if (impersonatedClientId) {
                                try { sessionStorage.removeItem('GOD_MODE_ID'); } catch (_) { /* ignore */ }
                                useAdminStore.setState({ impersonatedClientId: null, impersonatedClientName: null });
                            } else if (!userRoleInfo.isTeam) {
                                signOut(auth);
                            }
                        }}
                        className="mt-4 px-6 py-3 bg-transparent hover:bg-white/5 text-slate-400 hover:text-white font-bold uppercase tracking-widest text-xs rounded-xl border border-slate-700"
                    >
                        Abmelden
                    </button>
                </motion.div>
            )}
        </AnimatePresence>
    );

    // AutoUpdate IMMER zuletzt / über Session-Blockade — sonst sieht Browser 2 kein Zwangsupdate
    if (isAuthLoading) return <TeamBootScreen label="Anmeldung…" />;
    if (!authUser) return (
        <>
            <AuthGatewayPage />
            <AutoUpdateModal />
        </>
    );
    // Team-Auflösung muss fertig sein, bevor Onboarding/Mandanten-Bootstrap greifen
    if (!isTeamResolutionComplete) return <TeamBootScreen label="Portal wird geladen…" />;
    
    if (userRoleInfo.isTeam && !isShadowing) {
        if (!isInitialized) return <TeamBootScreen label="Coach-Portal wird geladen…" />;
        return (
            <>
                <BrowserRouter>
                    <ErrorBoundary componentName="CoachPortal">
                        <TeamPortalRoutes
                            role={userRoleInfo.role}
                            category={userRoleInfo.category}
                            email={authUser?.email}
                        />
                    </ErrorBoundary>
                </BrowserRouter>
                {sessionBlockadeModal}
                <AutoUpdateModal />
            </>
        );
    }

    // God-Mode lädt noch → Abbruch bringt dich ZURÜCK INS ADMIN-PORTAL (nicht in denselben Hänger)
    if (isShadowing && (!isInitialized || !clientData)) {
        return (
            <>
                <div className="min-h-screen bg-[#020617] text-slate-200 flex flex-col items-center justify-center gap-6 p-6 text-center">
                    <p className="text-sm font-bold uppercase tracking-widest text-[#E6C299]">Akte wird geladen…</p>
                    <p className="text-xs text-slate-500 max-w-sm">Falls das hängt: Button unten klicken. Danach bist du wieder im Admin-Bereich — nicht nochmal im God-Mode.</p>
                    <button
                        type="button"
                        onClick={() => {
                            try { sessionStorage.removeItem('GOD_MODE_ID'); } catch (_) { /* ignore */ }
                            useAdminStore.setState({ impersonatedClientId: null, impersonatedClientName: null });
                            useAppStore.setState({ isInitialized: true, cloudWriteLock: false });
                            window.location.replace('/');
                        }}
                        className="px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-black uppercase tracking-widest text-xs rounded-xl"
                    >
                        Abbrechen → zurück zum Admin
                    </button>
                </div>
                <AutoUpdateModal />
            </>
        );
    }

    // 🚨 Onboarding-Route: Mandant UND God-Mode (Shadow) müssen das Wizard sehen können
    if (window.location.pathname.includes('/onboarding')) {
        // Team ohne Shadow → kein freies Onboarding, zurück zur Admin-Übersicht
        if (userRoleInfo.isTeam && !isShadowing) {
            window.location.href = '/';
            return null;
        }

        // FALLEN-AUSGANG: Freigeschaltete Mandanten / Session-Lock dürfen NICHT im Wizard kleben
        // (früherer Bug hat /onboarding gesetzt — Reload hält sonst ewig fest)
        const sessionUnlockedEscape = (() => {
            try { return sessionStorage.getItem('PE_ADMIN_UNLOCKED') === '1'; } catch { return false; }
        })();
        if (
            !isShadowing &&
            isInitialized &&
            clientData &&
            (isSessionBlocked || clientData.isUnlockedByAdmin === true || sessionUnlockedEscape)
        ) {
            window.location.replace('/');
            return null;
        }

        return (
            <>
                <OnboardingWizard />
                {sessionBlockadeModal}
                <AutoUpdateModal />
            </>
        );
    }
    
    if (!isInitialized || !clientData) return <AutoUpdateModal />;

    const isPePapersUnlocked = unlockedPremiumTools.includes('pe-papers');
    const wantsPePapers = currentPage === 'pe-papers' || window.location.pathname.includes('/pe-papers');
    const isPePapersRoute = isPePapersUnlocked && wantsPePapers;

    // Zahlungsstop: Logik ist verdrahtet, Sperre aber erst bei STRIPE_PAYMENT_LOCK_ARMED=true
    const isPaymentLocked = shouldEnforcePaymentLock({
        client: clientData,
        isGodMode: isShadowing,
        isTeamUser: userRoleInfo.isTeam && !isShadowing,
    });

    // 🚨 ANTI-GEISTER-SCHRANKE: Nur explizit freigeschaltete Mandanten (=== true) dürfen die Kern-App sehen
    // Session-Flag PE_ADMIN_UNLOCKED bricht die Schleife Freigabe-Screen → / → /onboarding
    const sessionUnlocked = (() => {
        try { return sessionStorage.getItem('PE_ADMIN_UNLOCKED') === '1'; } catch { return false; }
    })();
    if (!userRoleInfo.isTeam && !isShadowing && !isSessionBlocked && clientData.isUnlockedByAdmin !== true && !sessionUnlocked) {
        if (!window.location.pathname.includes('/onboarding')) {
            localStorage.removeItem('PreisExpert_Data_V2');
            window.location.replace('/onboarding');
            return null;
        }
    }

    return (
        <>
            {effectiveIsYearLocked && (
                <style>{`
                    .panzerglas-active input:not([type="checkbox"]):not([type="radio"]),
                    .panzerglas-active textarea,
                    .panzerglas-active select {
                        pointer-events: none !important;
                        background-color: #1e293b !important;
                        color: #E6C299 !important;
                        border-color: rgba(230, 194, 153, 0.3) !important;
                        opacity: 1 !important;
                    }
                    .panzerglas-active input[type="checkbox"],
                    .panzerglas-active input[type="radio"] {
                        pointer-events: none !important;
                        filter: grayscale(100%) opacity(0.5);
                    }
                `}</style>
            )}

            {isShadowing && (
                <>
                    <div className="fixed top-0 left-0 right-0 h-10 bg-red-600 z-[9999] flex items-center justify-between gap-2 px-3 safe-area-pt text-white text-[10px] font-black uppercase tracking-widest shadow-xl max-w-[100vw] overflow-hidden">
                        <div className="flex gap-2 items-center min-w-0 flex-1 overflow-hidden">
                            <span className="truncate">⚠️ GOD-MODE: {impersonatedClientName}</span>
                        </div>
                        <button
                            type="button"
                            onClick={handleStopImpersonation}
                            className="shrink-0 bg-white text-red-600 px-3 py-1.5 rounded font-black hover:bg-red-50 transition-colors text-[11px] tracking-wider shadow-md"
                        >
                            BEENDEN
                        </button>
                    </div>
                    {/* Spacer: schiebt die Seite nach unten, damit der fixed-Balken nichts überdeckt */}
                    <div className="w-full h-10 shrink-0 safe-area-pt" aria-hidden />
                </>
            )}
            {/* God-Mode: kein overflow-x:hidden auf html/body — sonst kein Horizontal-Scroll auf Leistungsseiten */}

            <AnimatePresence>
                {exitWarningModal.isOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-[#15171e] border border-red-500/50 rounded-2xl p-8 max-w-md w-full shadow-[0_0_50px_rgba(220,38,38,0.3)] flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            </div>
                            <h2 className="text-2xl font-black text-white uppercase tracking-widest mb-4">Achtung!</h2>
                            <p className="text-slate-300 mb-8 text-sm leading-relaxed">
                                {exitWarningModal.reason === 'dirty' ? (
                                    <>
                                        Es gibt noch ungespeicherte Änderungen bzw. ein Speichern läuft.<br />
                                        <strong className="text-red-400">Wirklich rausgehen?</strong>
                                    </>
                                ) : (
                                    <>
                                        Die Datenerfassung ist noch nicht abgeschlossen.<br />
                                        <strong className="text-red-400">Wirklich rausgehen?</strong>
                                    </>
                                )}
                            </p>
                            <div className="flex gap-4 w-full">
                                <button 
                                    onClick={() => setExitWarningModal({ isOpen: false, action: null, reason: null })}
                                    className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold uppercase tracking-widest text-xs py-3 rounded-xl transition-colors border border-slate-700"
                                >
                                    Abbrechen
                                </button>
                                <button 
                                    onClick={confirmExitAction}
                                    className="flex-1 bg-red-600 hover:bg-red-500 text-white font-black uppercase tracking-widest text-xs py-3 rounded-xl transition-colors shadow-lg shadow-red-900/50 border border-red-50"
                                >
                                    Ja, Rausgehen
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>{isAdminLocked && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100000] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center text-center p-6">
                    <div className="w-24 h-24 bg-[#E6C299]/10 rounded-full flex items-center justify-center mb-6 border border-[#E6C299]/30">
                        <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2 }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#E6C299" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </motion.div>
                    </div>
                    <h2 className="text-[#E6C299] text-3xl font-black uppercase tracking-[0.2em] mb-4">System-Wartung</h2>
                    <p className="text-[#E6C299] text-xl font-bold mb-3">
                        {adminLockHolderLabel
                            ? `${adminLockHolderLabel} ist gerade in deiner Akte.`
                            : 'Dein PreisExpert-Team ist gerade in deiner Akte.'}
                    </p>
                    <p className="text-slate-400 max-w-md text-base leading-relaxed">
                        Deine Kalkulation wird gerade optimiert. Bitte warte einen Moment — sobald die Akte frei ist, kannst du weiterarbeiten.
                    </p>
                </motion.div>
            )}</AnimatePresence>

            <AnimatePresence>
                {isPaymentLocked && (
                    <ZahlungsstopModal
                        locked={true}
                        onLogout={() => {
                            try { sessionStorage.removeItem('GOD_MODE_ID'); } catch { /* ignore */ }
                            signOut(auth);
                        }}
                    />
                )}
            </AnimatePresence>

            {sessionBlockadeModal}
            {/* Nach Session-Blockade: Update muss klickbar über dem Wartemodal liegen */}
            <AutoUpdateModal />

            <AnimatePresence>{!isSystemBooted && <SystemBootSequence onComplete={setSystemBooted} />}</AnimatePresence>
            <Notification message={notification.msg} type={notification.type} onClear={() => setNotification({ msg: '', type: 'success' })} />
            <PremiumModal showNotification={showNotification} navigateTo={navigateToPage} />
            {isSystemBooted && !isSessionBlocked && !isAdminLocked && !isPaymentLocked && (
                <EventPromotionModal enabled={true} navigateTo={navigateToPage} />
            )}
            {/* PC-Safe nur echter Mandant — nie Gott-Modus/Coach */}
            {isSystemBooted && isInitialized && !isShadowing && !userRoleInfo.isTeam && (
                <ClientPcSafeManager
                    showNotification={showNotification}
                    enabled={!isSessionBlocked && !isAdminLocked}
                    manualOpen={pcSafeManualOpen}
                    onManualClose={() => setPcSafeManualOpen(false)}
                    logoutPromptOpen={pcSafeLogoutOpen}
                    onLogoutContinue={finishGlobalLogout}
                    onLogoutCancel={() => setPcSafeLogoutOpen(false)}
                />
            )}

            {isSystemBooted && (
                <div className="fixed right-0 top-16 md:top-20 z-[60] flex flex-col items-end gap-1.5 pointer-events-none">
                    {currentPage !== 'jahresuebersicht' && (
                        <button
                            type="button"
                            onClick={() => navigateToPage('jahresuebersicht')}
                            title="Zur Jahres-Akte"
                            aria-label="Zur Jahres-Akte springen"
                            className="pointer-events-auto hidden md:flex flex-col items-center justify-center gap-1 py-2.5 px-2 rounded-l-xl border-2 border-r-0 border-[#E6C299]/60 bg-[#E6C299]/15 dark:bg-gradient-to-l dark:from-[#1a1d24] dark:to-[#0f1015] text-pe-accent-text dark:text-[#E6C299] shadow-md hover:border-[#E6C299]/90 hover:bg-[#E6C299]/25 hover:scale-[1.03] transition-all duration-200"
                        >
                            <JahresAkteIcon size={12} />
                            <span className="text-[7px] font-black uppercase tracking-[0.18em] [writing-mode:vertical-rl] rotate-180 leading-none">
                                Jahres-Akte
                            </span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={toggleTheme}
                        title={isDarkTheme ? 'Light Mode aktivieren' : 'Dark Mode aktivieren'}
                        aria-label={isDarkTheme ? 'Zu Light Mode wechseln' : 'Zu Dark Mode wechseln'}
                        className="pointer-events-auto hidden md:flex flex-col items-center justify-center gap-1 py-2.5 px-2 rounded-l-xl border-2 border-r-0 border-pe-border-strong dark:border-[#333] bg-pe-card dark:bg-gradient-to-l dark:from-[#1a1d24] dark:to-[#0f1015] text-pe-muted dark:text-slate-300 shadow-md hover:text-pe-accent-text dark:hover:text-[#E6C299] hover:border-[#8D6435]/50 dark:hover:border-[#E6C299]/50 hover:scale-[1.03] transition-all duration-200"
                    >
                        {isDarkTheme ? <SunIcon size={12} /> : <MoonIcon size={12} />}
                        <span className="text-[7px] font-black uppercase tracking-[0.18em] [writing-mode:vertical-rl] rotate-180 leading-none">
                            {isDarkTheme ? 'Light' : 'Dark'}
                        </span>
                    </button>
                    {/* Premium nur noch im Top-Header / Sidebar — rechter Rand-Tab entfernt (Überlappung mit Betty) */}
                    <div id={RIGHT_EDGE_RAIL_SLOT_ID} className="flex flex-col items-end gap-2 pointer-events-auto empty:hidden" />
                </div>
            )}

            {/* Mobil: Akte sitzt in der Header-Buttonleiste (kein FAB über dem Inhalt) */}
            
            {/* Sidebar außerhalb CSS-zoom — volle Viewport-Höhe, kein gestrecktes Grau */}
            {isSystemBooted && (
                <div className="z-50">
                    <SmartSidebar
                        currentPage={currentPage}
                        onNavigate={navigateToPage}
                        currentYearStatus={currentYearStatus}
                        currentYear={currentYear}
                    />
                </div>
            )}

            <div
                className="flex min-h-screen h-auto overflow-visible bg-pe-bg"
                style={{
                    zoom: `${zoomLevel}%`,
                    display: isSystemBooted ? 'flex' : 'none',
                    /* Bei Zoom < 100%: Layout-Höhe erhöhen, sonst bleibt unten weiße Fläche */
                    minHeight:
                        zoomLevel > 0 && zoomLevel !== 100
                            ? `${(100 / zoomLevel) * 100}vh`
                            : '100vh',
                }}
            >
                {/* MOBILE-REVISION: ml-16 auf md:ml-16 — Platz für fixed Sidebar */}
                <div className="flex-1 flex flex-col ml-0 md:ml-16 min-w-0 relative h-auto overflow-visible">
                    
                    {(() => {
                        const headerPageTitle = isPePapersRoute
                            ? 'PEPapers'
                            : currentPageConfig?.key === 'vertragsuebersicht'
                              ? 'Vertragsübersicht'
                              : currentPageConfig?.key === 'datenuebersicht'
                                ? 'Daten-Übersicht'
                                : (currentPageConfig?.label || 'PreisExpert');
                        const headerVideoKey = isPePapersRoute
                            ? 'pe-papers'
                            : String(currentPage || '').startsWith('leistung-')
                              ? 'leistung'
                              : (currentPageConfig?.key || currentPage || 'jahresuebersicht');
                        const stickyTopClass = isShadowing ? 'top-10' : 'top-0';
                        const headerPadClass =
                            'px-[calc(0.5rem+1cm)] md:px-[calc(0.75rem+1cm)] lg:px-[calc(1rem+1cm)]';

                        const headerActions = (
                            <>
                                <div className="flex items-center justify-center xl:justify-end gap-1.5 sm:gap-2 w-full flex-wrap">
                                    <div className={`flex items-center gap-1.5 bg-pe-card/80 px-2.5 py-1.5 rounded-full border ${
                                        syncStatus === 'error' ? 'border-red-500/40' : 'border-pe-border'
                                    }`}>
                                        {syncStatus === 'syncing' ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin text-blue-400"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                                        ) : syncStatus === 'error' ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-400"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/><line x1="2" x2="22" y1="2" y2="22"/></svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={syncStatus === 'success' ? 'text-emerald-400' : 'text-slate-500'}><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/></svg>
                                        )}
                                        <span className={`text-[8px] font-black tracking-widest uppercase ${
                                            syncStatus === 'syncing' ? 'text-blue-400' : syncStatus === 'success' ? 'text-emerald-400' : syncStatus === 'error' ? 'text-red-400' : 'text-slate-500'
                                        }`}>
                                            {syncStatus === 'syncing' ? 'Speichert...' : syncStatus === 'success' ? 'Gespeichert' : syncStatus === 'error' ? 'Fehler' : 'Verbunden'}
                                        </span>
                                    </div>
                                    {!isShadowing && (
                                        <button onClick={handleClientDownload} className="flex items-center gap-1.5 px-3 py-1.5 bg-pe-card hover:bg-emerald-500/10 text-pe-muted hover:text-emerald-400 rounded-lg text-[10px] font-bold tracking-widest uppercase border border-pe-border hover:border-emerald-500/30 shadow-sm"><DownloadCloudIcon size={12} /> PC-Safe</button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={toggleTheme}
                                        title={isDarkTheme ? 'Light Mode' : 'Dark Mode'}
                                        aria-label={isDarkTheme ? 'Zu Light Mode wechseln' : 'Zu Dark Mode wechseln'}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-pe-card hover:bg-[#E6C299]/10 text-pe-muted hover:text-[#E6C299] rounded-lg text-[10px] font-bold tracking-widest uppercase border border-pe-border hover:border-[#E6C299]/30 shadow-sm"
                                    >
                                        {isDarkTheme ? <SunIcon size={12} /> : <MoonIcon size={12} />}
                                        {isDarkTheme ? 'Light' : 'Dark'}
                                    </button>
                                    {currentPage !== 'jahresuebersicht' && (
                                        <button
                                            type="button"
                                            onClick={() => navigateToPage('jahresuebersicht')}
                                            title="Zur Jahres-Akte"
                                            aria-label="Zur Jahres-Akte springen"
                                            className="md:hidden flex items-center gap-1.5 px-3 py-1.5 bg-[#E6C299]/15 hover:bg-[#E6C299]/25 text-[#E6C299] rounded-lg text-[10px] font-bold tracking-widest uppercase border border-[#E6C299]/40 shadow-sm"
                                        >
                                            <JahresAkteIcon size={12} />
                                            Akte
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={openPremiumModal}
                                        title="Premium-Tools öffnen"
                                        className="md:hidden flex items-center gap-1.5 px-3 py-1.5 bg-[#E6C299]/15 hover:bg-[#E6C299]/25 text-[#E6C299] rounded-lg text-[10px] font-bold tracking-widest uppercase border border-[#E6C299]/40 shadow-sm"
                                    >
                                        <span className="text-xs leading-none">🌟</span>
                                        Premium
                                    </button>
                                    <button onClick={handleGlobalLogout} className="flex items-center gap-1.5 px-3 py-1.5 bg-pe-card hover:bg-red-500/10 text-pe-muted hover:text-red-400 rounded-lg text-[10px] font-bold tracking-widest uppercase border border-pe-border hover:border-red-500/30 shadow-sm"><LogOutIcon size={12} /> Logout</button>
                                </div>
                            </>
                        );

                        return (
                            <>
                                {/* MOBIL: Logo + Titel + Video sticky unter God-Mode-Leiste */}
                                <div
                                    className={`md:hidden sticky ${stickyTopClass} z-40 w-full pt-2 pb-2 ${headerPadClass} bg-pe-bg/95 backdrop-blur-md border-b border-pe-border/60`}
                                >
                                    <div className="relative rounded-xl bg-pe-elevated border border-pe-border w-full max-w-[100rem] mx-auto flex flex-col items-center gap-1.5 px-3 py-2.5">
                                        <img
                                            src={logoSrc}
                                            alt="Logo"
                                            className="h-12 w-auto max-w-[10rem] object-contain"
                                        />
                                        <h2 className="text-pe-accent-text dark:text-[#E6C299] font-black uppercase tracking-wide text-sm italic leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-full px-1">
                                            {headerPageTitle}
                                        </h2>
                                        <PageVideoHelp pageKey={headerVideoKey} className="shrink-0 max-w-full" />
                                    </div>
                                </div>

                                {/* MOBIL: Buttons scrollen mit — nicht sticky */}
                                <div className={`md:hidden w-full pb-2 ${headerPadClass}`}>
                                    <div className="flex flex-col items-center gap-1.5 w-full">
                                        {headerActions}
                                    </div>
                                </div>

                                {/* DESKTOP: etwas höhere Leiste (Buttons + Dock innen), Logo ~+50 %, alles vertikal mittig */}
                                <header
                                    className={`hidden md:block flex-shrink-0 sticky z-40 w-full pt-4 pb-2 bg-pe-bg ${headerPadClass} ${stickyTopClass}`}
                                >
                                    <div className="relative rounded-xl bg-pe-elevated border border-pe-border w-full max-w-[100rem] mx-auto flex flex-col xl:flex-row xl:items-center xl:h-[7.5rem] gap-2 xl:gap-3 px-3 xl:pl-3 xl:pr-5 py-2.5 xl:py-2 overflow-visible">
                                        <div className="z-[1] flex items-center justify-center xl:justify-start shrink-0 xl:self-center">
                                            <img
                                                src={logoSrc}
                                                alt="Logo"
                                                className="h-20 sm:h-24 xl:h-[11.25rem] w-auto max-w-[16rem] xl:max-w-[20rem] object-contain object-left xl:-my-7"
                                            />
                                        </div>
                                        <div className="flex flex-col justify-center items-center flex-1 min-w-0 z-10 px-2 text-center gap-1.5 xl:self-center">
                                            <h2 className="text-pe-accent-text dark:text-[#E6C299] font-black uppercase tracking-wide sm:tracking-wider xl:tracking-widest text-sm sm:text-base lg:text-lg xl:text-xl 2xl:text-2xl italic leading-tight whitespace-nowrap overflow-hidden text-ellipsis max-w-full px-1">
                                                {headerPageTitle}
                                            </h2>
                                            <PageVideoHelp pageKey={headerVideoKey} className="shrink-0 max-w-full" />
                                        </div>
                                        <div className="flex flex-col items-center xl:items-end justify-center shrink-0 z-10 gap-1.5 w-full xl:w-max min-w-0 xl:self-center">
                                            {headerActions}
                                            <div className="hidden xl:block w-max max-w-none">
                                                <ProductivityDock showNotification={showNotification} syncStatus={syncStatus} />
                                            </div>
                                        </div>
                                    </div>
                                </header>

                                {/* Ein einziges Dock (mobil + Desktop unter xl) — kein Doppel-Mount */}
                                <div className={`w-full pb-2 xl:hidden ${headerPadClass}`}>
                                    <div className="flex flex-col items-center xl:items-end gap-1.5 w-full">
                                        <ProductivityDock showNotification={showNotification} syncStatus={syncStatus} />
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                    
                    <main id="app-main-scroll" className={`p-4 relative z-10 mx-auto pb-24 md:pb-4 ${isPePapersRoute ? 'max-w-7xl' : contentLayoutClass} w-full ${effectiveIsYearLocked ? 'panzerglas-active' : ''} flex-1 overflow-visible h-auto`}>
                        <ErrorBoundary componentName={isPePapersRoute ? 'PEPapers' : (currentPageConfig?.label || 'PageContent')}>
                            <PagePinGate currentPage={isPePapersRoute ? 'pe-papers' : currentPage}>
                                {isPePapersRoute ? (
                                    <PePapersPage />
                                ) : (
                                    PageComponent && (
                                        <PageComponent
                                            showNotification={showNotification}
                                            navigateTo={navigateToPage}
                                            currentYearStatus={currentYearStatus}
                                            currentYear={currentYear}
                                            {...(currentPageConfig?.props || {})}
                                        />
                                    )
                                )}
                            </PagePinGate>
                        </ErrorBoundary>
                    </main>
                </div>
            </div>

            {isSystemBooted && (
                <>
                    {/* Chrome außerhalb CSS-zoom — Buttons bleiben immer bedienbar */}
                    <ZoomControls />
                    <BettyChatWindow 
                        currentRouteName={isPePapersRoute ? 'PEPapers' : (currentPageConfig?.label || currentPage)} 
                        showNotification={showNotification} 
                    />
                </>
            )}

            <Tooltip 
                id="app-tooltip" 
                className="z-[100000] shadow-2xl font-bold border border-[#dcb07b]"
                style={{ 
                    backgroundColor: '#E6C299', 
                    color: '#08090b', 
                    borderRadius: '6px', 
                    padding: '8px 12px', 
                    fontSize: '12px', 
                    maxWidth: '300px', 
                    lineHeight: '1.4' 
                }} 
            />
        </>
    );
}

export default App;